import { computed, Inject, Injectable, Signal } from '@angular/core';
import { filter, map, tap, Observable, switchMap, combineLatest, firstValueFrom, from, finalize, of, catchError, EMPTY, throwError, BehaviorSubject, first, concatMap, mergeMap, toArray, scan, takeWhile, last, defaultIfEmpty } from 'rxjs';
import { RxActions } from '@rx-angular/state/actions';
import { DatePipe } from '@angular/common';
import { DialogRef } from '@ngneat/dialog';
 
import { ContentPipe } from 'src/app/services/content/content.pipe';
import { IsDateInPastPipe } from "../../shared/pipes/is-date-in-past.pipe";
import { SystemsQuery } from 'src/app/systems/state/systems.query';
import { SystemInstance } from 'src/app/systems/models/systems.interface';
import { FeatureFlagQuery } from 'src/app/feature-flag/state/feature-flag.query';
import { DropdownOption } from 'src/app/shared/mine-dropdown/mine-dropdown.interface';
import { FilterCategory } from 'src/app/core/models/filtering.interface';
import { MineSort } from 'src/app/shared/mine-sort/mine-sort.interface';
import { SortDirectionEnum } from 'src/app/shared/sort-direction.enum';
import { AiAssessmentsStore } from '../state/ai-assessments.store';
import { AssessmentActions } from '../models/assessment-actions.interface';
import { AssessmentExportMetadata, AssessmentExportTemplateInstance } from '../models/assessment-export.interface';
import { AssessmentPage, AssessmentQuestion, AssessmentSection, AssessmentsMineOSFormControl, AssessmentsMineOSTemplateResponse, AssessmentTableStatus } from '../models/assessments.interface';
import { AssessmentReference } from '../models/assessment-reference.interface';
import { AssessmentRegulation } from '../models/assessment-regulation.interface';
import { AiAssessmentsExportHelper } from './ai-assessments-export-helper';
import { CustomFieldsV2Service } from 'src/app/company-settings/custom-fields-v2/custom-fields-v2.service';
import { ProcessingActivity, ProcessingActivityPartial, ProcessingActivitySystemInfo } from 'src/app/api/models/processing-activities/processing-activities.interface';
import { CompanySettingsQuery } from 'src/app/company-settings/state/company-settings.query';
import { PdfGeneratorService } from 'src/app/pdf-generator/pdf-generator.service';
import { ExportTypeEnum } from 'src/app/pdf-generator/pdf-generator.interface';
import { ContentSchemaHelper } from 'src/app/services/content/content-schema-helper';
import { ProfileQuery } from 'src/app/profile/state/profile.query';
import { DomSanitizer } from '@angular/platform-browser';
import { TableCustomView } from 'src/app/api/models/custom-views/custom-views.interface';
import { DialogsManagerService } from 'src/app/services/dialogs-manager.service';
import { MineAsyncOperationSpinnerDialogComponent } from 'src/app/shared/mine-async-operation-spinner-dialog/mine-async-operation-spinner-dialog';
import { LoggerService } from 'src/app/logger/logger.service';
import { MineSnackbarService } from 'src/app/shared/mine-snackbar/mine-snackbar.service';
import { MineSnackbarType } from 'src/app/shared/mine-snackbar/mine-snackbar-type';
import { CustomFieldOption } from 'src/app/api/models/company-settings/custom-fields.interface';
import { RandomUUIDPipe } from 'src/app/shared/pipes/random-uuid.pipe';
import { API_CLIENT_ASSESSMENTS_SERVICE } from 'src/app/api/injectors/api-client-ai-assessments.token';
import { AiAssessmentsLockStatusService } from './ai-assessments-lock-status.service';
import { ProcessActivityStatusEnum } from 'src/app/api/models/processing-activities/processing-activities.enum';
import { BaseApiClientAssessmentService } from 'src/app/api/base-api-client-assessment.service';
import { AssessmentsLabelsService } from './assessments-labels.service';
import { AssessmentFilteringCategoryEnum, AssessmentFilteringGroupEnum, AssessmentUseCaseEnum } from '../models/assessments.enum';
import { AssessmentRow } from 'src/app/api/models/assessments/assessment-bulk.interface';
import { AssessmentAttachment, AssessmentCustomLabel, AssessmentDataTypeSource, AssessmentDropdownOption, AssessmentInvitee, AssessmentLockStatus, AssessmentPageRequest, AssessmentPageResponse, AssessmentResourceValue, AssessmentEditRequest, JsonPatchRequest, AssessmentResourceId, AssessmentUpdateReviewDateRequest, AssessmentSectionRequest, AssessmentEntity, AssessmentQuestionMoveRequest, AssessmentQuestionRequest, AssessmentCustomFieldDropdown, AssessmentCustomFieldText, AssessmentCommentRequest, AssessmentCommentResponse, AssessmentActivityLogsResponse, AssessmentAuditFilterOptionsResponse } from 'src/app/api/models/assessments/assessment.interface';
import { AssessmentCollaborationState, AssessmentColumnEnum, AssessmentEditOpEnum, AssessmentInviteeStatusEnum, AssessmentQuestionTypeEnum, AssessmentStatusEnum, AssessmentTemplateEnum, CollaboratorVisibilityScopeEnum } from 'src/app/api/models/assessments/assessment.enum';
import { CamelCaseWithSpacesPipe } from 'src/app/shared/pipes/camel-case-with-spaces.pipe';
import { SuggestedStateEnum } from 'src/app/api/models/systems/systems.enum';
import { AssessmentsTemplate, AssessmentsTemplateRequest } from 'src/app/api/models/assessments/assessment-template.interface';
import { AssessmentsLogicManagerService } from './assessments-logic-manager.service';
import { AiAssessmentsQuestionTypeHelper } from '../shared/question-type-helper';
import Fuse, { FuseResultMatch } from 'fuse.js';

import { buildAndQuery, buildDocFromParts, createFuse, extractMatchInfos, FuseDocItem, isQuerySearchable, SearchableDoc, SearchExtra } from 'src/app/shared/utils/fuzzy-search.util';
import { KeyedSerializer } from 'src/app/shared/utils/keyed-serializer';

export interface AssessmentSearchEnrichments {
    getExtras: (assessment: AssessmentPage) => readonly SearchExtra[];
}

const SEARCH_DEBUG_KEY = 'portal:debug-search';
import { AssessmentsTrackingChangesHelper } from './assessments-tracking-changes-helper';
import { AssessmentOperation, AssessmentOperationType, ChangesPayload, OrdinalityChangesPayload, SubmissionOperationResult, SubmissionState } from '../models/assessments-tracking-changes.interface';
import { AssessmentsMapperService } from './assessments-mapper.service';
import { HtmlDecodePipe } from 'src/app/shared/pipes/html-decode.pipe';
import { AssessmentsSplitByUseCaseService } from './assessments-split-by-use-case.service';
import { ProfileAgent } from 'src/app/api/models/profile/profile-agent';
import { AgentIdToNamePipe } from 'src/app/shared/pipes/agent-id-to-name.pipe';
import { isCollaborationRoute } from 'src/app/collaboration/collaborations.utils';
import { mergeFilterSelections } from '../utils/merge-filter-selections.util';

type StatefulConditionFunction = (
  control: AssessmentSection | AssessmentQuestion | AssessmentsMineOSFormControl,
  state: any
) => [boolean, any];
​
@Injectable({
     providedIn: 'root',
})
export class AssessmentsService {
    private readonly loggerName = 'AssessmentsService';

    // Cache is keyed by object reference. Correctness depends on
    // `updateInstanceInStore` producing a fresh AssessmentPage ref on every
    // store mutation (it spreads into a new object). The in-place mutation
    // helpers (skipSync paths in update*/remove* methods) call
    // `refreshSearchDoc` so the existing ref's entry stays in sync.
    // If any future code starts mutating a stored AssessmentPage in place
    // without going through those helpers it must call refreshSearchDoc
    // itself or the cache will serve a stale doc.
    private readonly searchDocCache = new WeakMap<AssessmentPage, { parts: readonly SearchExtra[]; doc: SearchableDoc }>();

    // Cache invalidates on either source-array or CMS content-array ref change.
    // Relies on contentPipe returning the same array/object reference until the
    // CMS content actually changes; switching to a CMS accessor that constructs
    // a fresh object per call would silently disable cache reuse.
    private cachedFuse: {
        source: AssessmentPage[];
        statusesRef: DropdownOption[] | undefined;
        typesRef: Record<string, string> | undefined;
        fuse: Fuse<FuseDocItem<AssessmentPage>>;
        keys: string[];
    } | null = null;
    readonly CROSS_FUNCTIONAL_FUNCTION_NAME = "Cross functional";
	private functions: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
	functions$ = this.functions.asObservable();

    readonly DEFAULT_TEMPLATE = {
        type: 'AI',
        name: 'AI Assessment',
    };
    
    private _FORM_TEMPLATES: AssessmentsMineOSTemplateResponse[];

    /**
     * MineOS predefined templates, resolved lazily from CMS on first use. Must NOT be built in the
     * constructor: this root service can be constructed during app bootstrap (via the risks graph)
     * before `ai-assessments` content loads, which made `transform(...).concat()` throw. The getter
     * retries until content is present. See [[reference_contentpipe_construction_crash]].
     */
    private get FORM_TEMPLATES(): AssessmentsMineOSTemplateResponse[] {
        this.initMineOSTemplates();
        return this._FORM_TEMPLATES ?? [];
    }

    private dialogRef: DialogRef<MineAsyncOperationSpinnerDialogComponent>;

    private randomUUIDPipe = new RandomUUIDPipe();

    private camelCaseWithSpacesPipe = new CamelCaseWithSpacesPipe();

    // Here for the remaining life time of this service
    private fullFiltersList: FilterCategory[];
    //

    private readonly isCollaborator = isCollaborationRoute();

    private readonly MAX_CONCURRENT_OPERATIONS = 5;
    private readonly questionPatchQueue = new KeyedSerializer<AssessmentResourceId>();

    private readonly PARALLELIZABLE_OPERATIONS: ReadonlySet<AssessmentOperationType> = new Set([
        'question_update', 'section_update', 'assessment_update',
    ]);

    constructor(
        private store: AiAssessmentsStore,
        private systemsQuery: SystemsQuery,
        private profileQuery: ProfileQuery,
        private featureFlagQuery: FeatureFlagQuery,
        private customFieldsV2Service: CustomFieldsV2Service,
        private companySettingsQuery: CompanySettingsQuery,
        private questionTypeHelper: AiAssessmentsQuestionTypeHelper,
        private aiAssessmentsExportHelper: AiAssessmentsExportHelper,
        private pdfGeneratorService: PdfGeneratorService,
        private isDateInPastPipe: IsDateInPastPipe,
        private htmlDecodePipe: HtmlDecodePipe,
        private sanitizer: DomSanitizer,
        private contentPipe: ContentPipe,
        private datePipe: DatePipe,
        private dialogService: DialogsManagerService,
        private loggerService: LoggerService,
        private snackbarService: MineSnackbarService,
        private assessmentsLogicManagerService: AssessmentsLogicManagerService,
        private aiAssessmentsLockStatusService: AiAssessmentsLockStatusService,
        private aiAssessmentsLabelsService: AssessmentsLabelsService,
        private assessmentsTrackingChangesHelper: AssessmentsTrackingChangesHelper,
        private assessmentsMapperService: AssessmentsMapperService,
        private assessmentsSplitByUseCaseService: AssessmentsSplitByUseCaseService,
        private agentIdToNamePipe: AgentIdToNamePipe,
        @Inject(API_CLIENT_ASSESSMENTS_SERVICE) private apiClientAssessmentService: BaseApiClientAssessmentService,
    ) {
        const functions = Object.assign([], this.contentPipe.transform('ai-assessments.functions'));
        this.setFunctions(functions);
    }

    createAssessment(request: AssessmentPageRequest): Observable<AssessmentPage> {        
        return this.apiClientAssessmentService.createAssessment(request).pipe(
            map(assessmentPageRes => this.mapInstanceOrigianlStateAndStore(assessmentPageRes)),
        );
    }

    updateAssessment(pageId: AssessmentResourceId, request: AssessmentEditRequest[], skipSync = false): Observable<AssessmentPage> {
        return this.apiClientAssessmentService.updateAssessment(pageId, request).pipe(
            map(assessmentPageRes => skipSync ? assessmentPageRes as AssessmentPage : this.mapInstanceOrigianlStateAndStore(assessmentPageRes)),
        );
    }

    updateAssessmentState(pageId: AssessmentResourceId, state: AssessmentStatusEnum): Observable<AssessmentPage> {
        return this.apiClientAssessmentService.updateInstanceState(pageId, state).pipe(
            map(assessmentPageRes => this.mapInstanceOrigianlStateAndStore(assessmentPageRes)),
        );
    }

    updateAssessmentReviewDate(instance: AssessmentPage, reviewDate?: string, ownerId?: string): Observable<AssessmentPage> {
        const payload: AssessmentUpdateReviewDateRequest = {
            instanceId: instance?.id,
            reviewDate,
            ownerId,
            setNoFutureReminders: !reviewDate
        };

        return this.apiClientAssessmentService.updateInstanceState(instance?.id, AssessmentStatusEnum.Completed).pipe(
            first(),
            switchMap(() => this.apiClientAssessmentService.setReviewDate(payload)),
            first(),
            map(assessmentPageRes => this.mapInstanceOrigianlStateAndStore(assessmentPageRes)),
        );
    }
    
    updateAssessmentName(pageId: AssessmentResourceId, newName: string): Observable<AssessmentPage> {
        return this.apiClientAssessmentService.updateAssessment(pageId, [{ op: AssessmentEditOpEnum.Replace, path: '/name', value: newName }]).pipe(
            first(),
            map(assessmentPageRes => this.mapInstanceOrigianlStateAndStore(assessmentPageRes)),
        );
    }

    deleteAssessment(pageId: AssessmentResourceId): Observable<void> {
        return this.apiClientAssessmentService.deleteAssessment(pageId).pipe(
            tap(() => this.removeInstanceFromStore(pageId))
        );
    }
    
    cloneAssessment(assessmentId: AssessmentResourceId, name: string): Observable<AssessmentPage> {
        return this.apiClientAssessmentService.cloneAssessment(assessmentId, name).pipe(
            map(assessmentPageRes => this.mapInstanceOrigianlStateAndStore(assessmentPageRes)),
        );
    }

    createSection(assessmentId: AssessmentResourceId, section: AssessmentSectionRequest, skipSync = false): Observable<AssessmentSection> {
        return this.apiClientAssessmentService.createAssessmentSection(assessmentId, section).pipe(
            map(sectionRes => this.assessmentsMapperService.mapAssessmentSection(assessmentId, sectionRes)),
            map(section => this.updateSectionInAssessment(assessmentId, section, skipSync)),
        );
    }

    updateSection(assessmentId: AssessmentResourceId, sectionId: AssessmentResourceId, section: AssessmentEditRequest[], skipSync = false): Observable<AssessmentSection> {
        return this.apiClientAssessmentService.updateAssessmentSection(assessmentId, sectionId, section).pipe(
            map(sectionRes => this.assessmentsMapperService.mapAssessmentSection(assessmentId, sectionRes)),
            map(section => this.updateSectionInAssessment(assessmentId, section, skipSync)),
        );
    }

    deleteSection(assessmentId: AssessmentResourceId, sectionId: AssessmentResourceId): Observable<AssessmentPage> {
        return this.apiClientAssessmentService.deleteAssessmentSection(assessmentId, sectionId).pipe(
            map(() => this.removeSectionFromAssessment(assessmentId, sectionId)),
        );
    }

    createQuestion(assessmentId: AssessmentResourceId, question: AssessmentQuestionRequest, skipSync = false): Observable<AssessmentQuestion> {
        const { assessmentSectionId, ...data } = question;
        return this.apiClientAssessmentService.createAssessmentQuestion(assessmentId, assessmentSectionId, data as AssessmentQuestionRequest).pipe(
            map(questionRes => this.assessmentsMapperService.mapAssessmentQuestion(assessmentId, assessmentSectionId, questionRes as AssessmentQuestion)),
            map(question => this.updateQuestionInAssessment(assessmentId, assessmentSectionId, question, skipSync)),
        );
    }

    updateQuestion(assessmentId: AssessmentResourceId, sectionId: AssessmentResourceId, questionId: AssessmentResourceId, question: AssessmentEditRequest[], skipSync = false):
        Observable<AssessmentQuestion> {
        return this.questionPatchQueue.serialize(questionId, () =>
            this.apiClientAssessmentService.updateAssessmentQuestion(assessmentId, sectionId, questionId, question).pipe(
                map(questionRes => this.assessmentsMapperService.mapAssessmentQuestion(assessmentId, sectionId, questionRes as AssessmentQuestion)),
                map(questionRes => this.updateQuestionInAssessment(assessmentId, sectionId, questionRes as AssessmentQuestion, skipSync)),
            ),
        );
    }

    deleteQuestion(assessmentId: AssessmentResourceId, sectionId: AssessmentResourceId, questionId: AssessmentResourceId): Observable<AssessmentPage> {
        return this.apiClientAssessmentService.deleteAssessmentQuestion(assessmentId, sectionId, questionId).pipe(
            map(() => this.removeQuestionFromAssessment(assessmentId, sectionId, questionId)),
        );
    }

    /**
     * Re-parents an existing question to another section (keeps its id/value/history).
     * The store is refreshed by the `assessment_reload` that closes the ordinality phase,
     * so no local mutation is needed here beyond mapping the response.
     */
    moveQuestionToSection(assessmentId: AssessmentResourceId, request: AssessmentQuestionMoveRequest): Observable<AssessmentQuestion> {
        return this.apiClientAssessmentService.moveAssessmentQuestionToSection(assessmentId, request).pipe(
            map(questionRes => this.assessmentsMapperService.mapAssessmentQuestion(assessmentId, request.targetSectionId, questionRes as AssessmentQuestion)),
        );
    }

    private updateSectionInAssessment(assessmentId: AssessmentResourceId, section: AssessmentSection, skipSync = false): AssessmentSection {
        const assessment = this.getAssessmentById(assessmentId);
        if (assessment) {
            const index = assessment.sections.findIndex(s => s.id === section.id);
            if (index !== -1) {
                assessment.sections[index] = section;
            }
            else {
                assessment.sections.push(section);
            }
            this.refreshSearchDoc(assessment);
            if (!skipSync) {
                assessment.updatedDate = new Date().toISOString();
                this.mapInstanceOrigianlStateAndStore(assessment);
            }
        }
        return section;
    }

    private removeSectionFromAssessment(assessmentId: AssessmentResourceId, sectionId: AssessmentResourceId): AssessmentPage {
        const assessment = this.getAssessmentById(assessmentId);
        if (assessment) {
            assessment.sections = assessment.sections.filter(s => s.id !== sectionId);
            assessment.layout = this.assessmentsTrackingChangesHelper.getOrdinality(assessment.sections, 'section');
            for (const section of assessment.sections) {
                section.layout = this.assessmentsTrackingChangesHelper.getOrdinality(section.questions, 'question');
            }
            this.refreshSearchDoc(assessment);
            this.mapInstanceOrigianlStateAndStore(assessment);
        }
        return assessment;
    }

    private updateQuestionInAssessment(assessmentId: AssessmentResourceId, sectionId: AssessmentResourceId, question: AssessmentQuestion, skipSync = false): AssessmentQuestion {
        const assessment = this.getAssessmentById(assessmentId);
        if (assessment) {
            const section = assessment.sections.find(s => s.id === sectionId);
            if (section) {
                // Prefer matching by persistent ID
                let index = section.questions.findIndex(q => q.id === question.id);

                // For newly created questions, the existing local question will not have an ID yet,
                // but it will share the same formControlId. In that case, replace the local one
                // instead of pushing a duplicate question.
                if (index === -1 && question.formControlId) {
                    index = section.questions.findIndex(q => q.formControlId === question.formControlId);
                }

                if (index !== -1) {
                    section.questions[index] = question;
                } else {
                    section.questions.push(question);
                }

                this.refreshSearchDoc(assessment);
                if (!skipSync) {
                    assessment.updatedDate = new Date().toISOString();
                    this.mapInstanceOrigianlStateAndStore(assessment);
                }
            }
        }
        return question;
    }

    private removeQuestionFromAssessment(assessmentId: AssessmentResourceId, sectionId: AssessmentResourceId, questionId: AssessmentResourceId): AssessmentPage {
        const assessment = this.getAssessmentById(assessmentId);
        if (assessment) {
            const section = assessment.sections.find(s => s.id == sectionId);
            if (section) {
                section.questions = section.questions.filter(q => q.id != questionId);
                section.layout = this.assessmentsTrackingChangesHelper.getOrdinality(section.questions, 'question');
                this.refreshSearchDoc(assessment);
                this.mapInstanceOrigianlStateAndStore(assessment);
            }
        }
        return assessment;
    }

    private initMineOSTemplates(): void {
        if (this._FORM_TEMPLATES) {
            return;
        }
        const templates = this.contentPipe.transform('ai-assessments.templates') as AssessmentsMineOSTemplateResponse[];
        if (!templates) {
            return; // ai-assessments content not loaded yet — retry on next access
        }
        const allTemplates = templates.concat({
                dataSourceSpecific: true,
                ...this.contentPipe.transform('ai-assessments.vendorTemplate') as AssessmentsMineOSTemplateResponse
        });
        this._FORM_TEMPLATES = this.withRegulationLabels(allTemplates);
    }

    /**
     * Tag each out-of-the-box template with its regulation's labels (CMS `ai-assessments.regulations`,
     * keyed by the enum value) so the gallery filter/sort, the template preview, and the
     * create-from-template flow all carry the expected labels — e.g. PIA → ["Privacy", "PIA"].
     * Templates that already declare their own tags are left untouched.
     */
    private withRegulationLabels(templates: AssessmentsMineOSTemplateResponse[]): AssessmentsMineOSTemplateResponse[] {
        const regulations = (this.contentPipe.transform('ai-assessments.regulations') as AssessmentRegulation[]) ?? [];
        const labelsByValue = new Map(regulations.map(regulation => [regulation.value, regulation.labels ?? []]));
        return templates.map(template => {
            if ((template.tags?.length ?? 0) > 0) {
                return template;
            }
            // `template.type` may be the enum KEY or VALUE; regulations are keyed by the VALUE.
            const value = (AssessmentTemplateEnum as Record<string, string>)[template.type] ?? template.type;
            const labels = labelsByValue.get(value);
            return labels?.length ? { ...template, tags: [...labels] } : template;
        });
    }

    private selectCustomFieldsLoading(): Observable<boolean | undefined> {
        return this.customFieldsV2Service.selectLoading();
    }

    init(): void {
        if (this.getLoading()() === undefined) {
            this.loggerService.debug(this.loggerName, 'Start init()');
            this.setLoading(true);

            this.selectCustomFieldsLoading().pipe(
                filter(loading => !loading),
                switchMap(() => combineLatest([
                    this.store.initAssessments(),
                    this.store.initTemplates()
                ])),
                first(),
                tap(([assessments, templates]) => this.afterInit(assessments, templates)),
            )
            .subscribe();
        }
    }

    private afterInit(
        assessments: Map<AssessmentUseCaseEnum, Map<AssessmentResourceId, AssessmentPage>>, 
        templates: Map<AssessmentUseCaseEnum, Map<AssessmentResourceId, AssessmentsTemplate>>
    ): void {
        this.setEmpty(!assessments?.size || !templates?.size);
        this.setLoading(false);
    }

    /** Refetch templates into the store — e.g. after a "Create a template with AI" run finishes
     *  (the AiTemplateReady bell), so the new template shows in the gallery and is available to open
     *  without a manual page refresh. `initTemplates()` completes itself (first + catchError). */
    refreshTemplates(): void {
        // initTemplates() completes itself (first + catchError); the error guard is a defensive no-op
        // so any secondary throw never surfaces as an unhandled rejection.
        this.store.initTemplates().subscribe({ error: () => undefined });
    }

    getEmpty(): Signal<boolean | undefined> {
        return this.store.signal('empty');
    }

    setEmpty(empty: boolean): void {
        this.store.set({ 'empty': empty });
    }

    setLoading(loading: boolean): void {
        this.store.set({ 'loading': loading });
    }
    
    setDisabled(disabled: boolean): void {
        this.store.set({ 'disabled': disabled });
    }
    
    getLoading(): Signal<boolean | undefined> {
        return this.store.signal('loading');
    }
        
    selectLoading(): Observable<boolean> {
        return this.store.select('loading');
    }
        
    getDisabled(): Signal<boolean | undefined> {
        return this.store.signal('disabled');
    }
    
    selectDisabled(): Observable<boolean> {
        return this.store.select('disabled');
    }
    
    async getSystemsByAssessmentId(assessmentId: string): Promise<SystemInstance[]> {
        const assessment = await this.selectAssessmentById(assessmentId);
        const systemsIds = this.getRelatedEntitiesByAssessment(assessment, AssessmentQuestionTypeEnum.DataSources) as AssessmentResourceId[];
        return this.systemsQuery.getSystemsByIds(systemsIds as string[]);
    }

    getAssessments(asArray: boolean = false, useCase: AssessmentUseCaseEnum = undefined): Signal<Map<AssessmentResourceId, AssessmentPage> | AssessmentPage[]> {
        if (asArray) {
            return computed(() => {
                const assessments = this.store.signal('assessments')();
                if (useCase) {
                    return Array.from(new Set([...(assessments?.get(useCase)?.values() ?? [])]));
                }

                return Array.from(new Set([...(assessments?.values() ?? [])]))
                    .flatMap(assessment => Array.from(assessment?.values() ?? []));
            });
        }

        return computed(() => {
            const assessments = this.store.signal('assessments')();
            if (useCase) {
                return assessments?.get(useCase) as Map<AssessmentResourceId, AssessmentPage>;
            }

            // Flatten all inner maps from the parent templates map into a single map
            return Array.from((assessments as Map<AssessmentUseCaseEnum, Map<AssessmentResourceId, AssessmentPage>>)?.values() ?? []).reduce((acc, map) => {
                if (map instanceof Map) {
                    map.forEach((value, key) => acc.set(key, value));
                }
                return acc;
            }, new Map<AssessmentResourceId, AssessmentPage>());
        });
    }
    
    getAssessmentById(id: AssessmentResourceId): AssessmentPage {
        return (this.getAssessments(true)() as AssessmentPage[])?.find(assessment => assessment.id === id) as AssessmentPage;
    }

    getAssessmentSignalById(id: AssessmentResourceId): Signal<AssessmentPage> {
        return computed(() => {
            const assessments = this.getAssessments(true)() as AssessmentPage[];
            return assessments.find(assessment => assessment.id === id);
        });
    }

    getActivitiesBySystemId(systemId: string): Signal<ProcessingActivityPartial[]> {
        return computed(() => {
            const activitiesMap = new Map<string, ProcessingActivityPartial>();
            
            this.getPaAssessments()()
                .filter(assessment =>
                    (this.getRelatedEntitiesByAssessment(assessment, AssessmentQuestionTypeEnum.DataSources) as AssessmentResourceId[])
                        .some(id => id === systemId)
                )
                .map(assessment => this.mapPaAssessmentToProcessingActivity(assessment))
                .forEach(activity => {
                    const sanitizedName = this.sanitizeAssessmentName(activity.name);
                    if (sanitizedName) { // Only process activities with valid names
                        activitiesMap.set(sanitizedName, activity);
                    }
                });
                
            return Array.from(activitiesMap.values())
                .sort((a, b) => a.name?.localeCompare(b.name) || 0);
        });
    }

    getPaAssessments(): Signal<AssessmentPage[]> {
        return computed(() => {
            return (this.getAssessments(true)() as AssessmentPage[])?.filter(assessment => assessment.type === AssessmentTemplateEnum.PA);
        });
    }

    getPaAssessment(id: AssessmentResourceId): Signal<AssessmentPage> {
        return computed(() => {
            const paAssessments = this.getPaAssessments()();
            return paAssessments.find(assessment => assessment.id === id);
        });
    }

    getPaAssessmentsAsProcessingActivities(): Signal<ProcessingActivityPartial[]> {
        return computed(() => {
            return this.getPaAssessments()().map(assessment => 
                this.mapPaAssessmentToProcessingActivity(assessment));
        });
    }

    getPaAssessmentAsProcessingActivity(id: AssessmentResourceId): Signal<ProcessingActivityPartial> {
        return computed(() => {
            const activities = this.getPaAssessmentsAsProcessingActivities()();
            return activities.find(activity => activity?.id === id);
        });
    }

    selectSystemsWithPaAssessmentsMap(): Signal<Map<AssessmentResourceId, ProcessingActivityPartial[]>> {
        return computed(() => { 
            const processingActivities = this.getPaAssessments()();
            return this.getSystemsWithPaAssessmentsMap(processingActivities);
        });
    }

    private getSystemsWithPaAssessmentsMap(paAssessments: AssessmentPage[]): Map<AssessmentResourceId, ProcessingActivityPartial[]> {
        const systemsWithPa = new Map<AssessmentResourceId, Map<string, ProcessingActivityPartial>>();

        paAssessments?.forEach(paAssessment => {
            const relatedSystems = this.getRelatedEntitiesByAssessment(paAssessment, AssessmentQuestionTypeEnum.DataSources) as AssessmentResourceId[];
                
            if (relatedSystems.length) { 
                const pa: ProcessingActivityPartial = 
                    this.mapPaAssessmentToProcessingActivity(paAssessment);

                relatedSystems.forEach(systemId => {
                    const sanitizedName = this.sanitizeAssessmentName(pa.name);
                    if (!sanitizedName) return; // Skip activities with invalid names
                    
                    if (!systemsWithPa.has(systemId)) {
                        systemsWithPa.set(systemId, new Map<string, ProcessingActivityPartial>());
                    }
                    
                    // Use sanitized name as key to prevent duplicates
                    systemsWithPa.get(systemId).set(sanitizedName, pa);
                });
            }
        });

        // Convert Map<string, Map<string, PA>> to Map<string, PA[]>
        const result = new Map<AssessmentResourceId, ProcessingActivityPartial[]>();
        systemsWithPa.forEach((activitiesMap, systemId) => {
            const activities = Array.from(activitiesMap.values())
                .sort((a, b) => a.name?.localeCompare(b.name) || 0);
            result.set(systemId, activities);
        });

        return result;
    }

    mapPaAssessmentToProcessingActivity(paAssessment: AssessmentPage, suggestionId?: string): ProcessingActivityPartial {
        let systems: { [key: string]: ProcessingActivitySystemInfo } = {};
        const relatedSystems = this.getRelatedEntitiesByAssessment(paAssessment, AssessmentQuestionTypeEnum.DataSources) as AssessmentResourceId[];
        if (relatedSystems.length) {
            systems = relatedSystems.reduce((acc, systemId) => {
                const system = this.systemsQuery.getEntity(systemId);
                return {
                    [systemId]: {
                        id: systemId,
                        name: system?.name ?? '',
                        iconPath: system?.iconPath ?? '',
                        extendedDataTypes: system?.extendedDataTypes ?? []
                    } as ProcessingActivitySystemInfo};
            }, {});
        }
       
        const relatedDataSubjectCategories = this.getRelatedEntitiesByAssessment(paAssessment, AssessmentQuestionTypeEnum.DataSubjects) as AssessmentEntity[];
        let dataSubjectCategories: string[] = [];
        if (relatedDataSubjectCategories.length) {
            dataSubjectCategories = relatedDataSubjectCategories.map(ds => {
                const id = ds.id?.toString();
                return this.customFieldsV2Service.getOption(id)?.text ?? id ?? '';
            });
        }
        
        return {
            id: suggestionId ?? paAssessment.id as string,
            name: paAssessment.name,
            dataSubjectCategories,
            systems
        };
    }

    selectPaActivitiesWithDataTypeOfSystem(systemId: string, dataTypeId: string, paStatus: ProcessActivityStatusEnum): Observable<ProcessingActivity[]> {
		return this.selectAssessments(true).pipe(
            map((assessments: AssessmentPage[]) => assessments
                .filter(a => a.type === AssessmentTemplateEnum.PA)
                .filter(a => {
                    switch (paStatus) {
                        case ProcessActivityStatusEnum.Draft:
                            return a.state === AssessmentStatusEnum.Draft;
                        case ProcessActivityStatusEnum.Operational:
                            return a.state === AssessmentStatusEnum.Completed;
                    }
                })
                .filter(a => {
                    const relatedSystems = this.getRelatedEntitiesByAssessment(a, AssessmentQuestionTypeEnum.DataSources) as AssessmentResourceId[];
                    if (!relatedSystems?.find(id => id === systemId)) return false;
                    const system = this.systemsQuery.getEntity(systemId);
                    return system.extendedDataTypes?.filter(e => e.state === SuggestedStateEnum.Accepted).find(e => e.id === dataTypeId);
                })
                .map(a => this.mapPaAssessmentToProcessingActivity(a) as ProcessingActivity)
            )
        );
	}

	selectPaActivitiesWithSingleDataTypeOfSystem(systemId: string, dataTypeId: string): Observable<ProcessingActivity[]> {
        return this.selectAssessments(true).pipe(
            map((assessments: AssessmentPage[]) => assessments
                .filter(a => a.type === AssessmentTemplateEnum.PA)
                .filter(a => {
                    const relatedSystems = this.getRelatedEntitiesByAssessment(a, AssessmentQuestionTypeEnum.DataSources) as AssessmentResourceId[];
                    if (!relatedSystems?.find(id => id === systemId)) return false;
                    const system = this.systemsQuery.getEntity(systemId);
                    return system.extendedDataTypes?.filter(e => e.state === SuggestedStateEnum.Accepted).find(e => e.id === dataTypeId);
                })
                .map(a => this.mapPaAssessmentToProcessingActivity(a) as ProcessingActivity)
            )
        );
	}

    /** Every assessment whose `DataTypes` question attributes each data type to the given system, keyed by data type id. */
    selectAssessmentsByDataTypeId(systemId: string): Observable<Map<string, AssessmentReference[]>> {
        return (this.selectAssessments(true) as Observable<AssessmentPage[]>).pipe(
            map(assessments => this.groupAssessmentsByDataTypeId(assessments, systemId)),
        );
    }

    private groupAssessmentsByDataTypeId(assessments: AssessmentPage[], systemId: string): Map<string, AssessmentReference[]> {
        const assessmentsByDataTypeId = new Map<string, Map<string, AssessmentReference>>();
        assessments?.forEach(assessment => this.collectAssessmentByDataTypeId(assessment, systemId, assessmentsByDataTypeId));

        // Keyed by id, not name: two assessments may share a name and both must stay counted and linkable.
        return new Map(Array.from(assessmentsByDataTypeId, ([dataTypeId, byId]) =>
            [dataTypeId, Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name))]));
    }

    private collectAssessmentByDataTypeId(assessment: AssessmentPage, systemId: string, assessmentsByDataTypeId: Map<string, Map<string, AssessmentReference>>): void {
        if (!assessment?.id || !this.sanitizeAssessmentName(assessment?.name)) return;

        // The system must be one of the assessment's linked data sources (or its specific vendor) —
        // a data type attributed to the system in an assessment that no longer lists it as a source must not count.
        const relatedSystems = this.getRelatedEntitiesByAssessment(assessment, AssessmentQuestionTypeEnum.DataSources);
        const relatedSystemIds = Array.isArray(relatedSystems) ? relatedSystems as AssessmentResourceId[] : [];
        if (!relatedSystemIds.some(id => String(id) === systemId)) return;

        const dataTypeSources = this.getRelatedEntitiesByAssessment(assessment, AssessmentQuestionTypeEnum.DataTypes);
        const dataTypeIds = (Array.isArray(dataTypeSources) ? dataTypeSources as AssessmentDataTypeSource[] : [])
            .filter(source => this.isDataTypeSourcedFromSystem(source, systemId))
            .map(source => source?.dataType?.id)
            .filter(dataTypeId => !!dataTypeId);

        dataTypeIds.forEach(dataTypeId => {
            const byId = assessmentsByDataTypeId.get(String(dataTypeId)) ?? new Map<string, AssessmentReference>();
            byId.set(String(assessment.id), { id: assessment.id, name: assessment.name });
            assessmentsByDataTypeId.set(String(dataTypeId), byId);
        });
    }

    // Caller guarantees the assessment is already linked to `systemId` via its DataSources question.
    // An empty per-type source list therefore passes (source dropdowns are optional; blank = all the assessment's linked systems).
    private isDataTypeSourcedFromSystem(source: AssessmentDataTypeSource, systemId: string): boolean {
        return !source?.dataSources?.length || source.dataSources.some(id => String(id) === systemId);
    }

    selectAssessments(asArray: boolean = false, useCase: AssessmentUseCaseEnum = undefined): Observable<Map<AssessmentResourceId, AssessmentPage> | AssessmentPage[]> {
        if (asArray) {
            if (useCase) {
                return this.store.select('assessments').pipe(
                    map(assessments => Array.from(new Set([...assessments.get(useCase)?.values() ?? []])))
                );
            }

            return this.store.select('assessments').pipe(
                map(assessments => Array.from(new Set([...assessments.values()]))
                    .flatMap(assessment => Array.from(assessment.values())))
            );
        }

        if (useCase) {
            return this.store.select('assessments').pipe(
                map(assessments => assessments.get(useCase) as Map<AssessmentResourceId, AssessmentPage>)
            );
        }

        return this.store.select('assessments').pipe(
            map(assessments => Array.from((assessments as Map<AssessmentUseCaseEnum, Map<AssessmentResourceId, AssessmentPage>>)?.values() ?? []).reduce((acc, map) => {
                if (map instanceof Map) {
                    map.forEach((value, key) => acc.set(key, value));
                }
                return acc;
            }, new Map<AssessmentResourceId, AssessmentPage>()))
        );
    }

    selectAssessmentsWithRisks(asArray: boolean = false): Observable<Map<AssessmentResourceId, AssessmentPage> | AssessmentPage[]> {
        const assessments$ = this.selectAssessments(asArray);
        return assessments$.pipe(
            map(assessments => {
                if (asArray) {
                    return (assessments as AssessmentPage[]).filter(assessment => {
                        const risks = this.getRelatedEntitiesByAssessment(assessment, AssessmentQuestionTypeEnum.Risks) as AssessmentResourceId[];
                        return risks?.length > 0;
                    });
                }
                const assessmentsWithRisks = new Map<AssessmentResourceId, AssessmentPage>();
                (assessments as Map<AssessmentResourceId, AssessmentPage>).forEach(assessment => {  
                    const risks = this.getRelatedEntitiesByAssessment(assessment, AssessmentQuestionTypeEnum.Risks) as AssessmentResourceId[];
                    if (risks?.length) {
                        assessmentsWithRisks.set(assessment.id, assessment);
                    }
                });
                return assessmentsWithRisks;
            })
        );
    }

    selectAssessmentById(id: AssessmentResourceId): Promise<AssessmentPage> {
        return firstValueFrom(this.selectAssessments().pipe(
            map((assessmentMap: Map<AssessmentResourceId, AssessmentPage>) => assessmentMap.get(id)),
        ));
    }

    selectAssessmentByIds(ids: AssessmentResourceId[]): Promise<AssessmentPage[]> {
        const instances: AssessmentPage[] = [];
        return firstValueFrom(this.selectAssessments().pipe(
            map((assessmentMap: Map<AssessmentResourceId, AssessmentPage>) => {
                ids.forEach(id => instances.push(assessmentMap.get(id)));
                return instances;
            }),
        ));
    }

    selectAssessmentByName(name: string): Promise<AssessmentPage> {
        return firstValueFrom(this.selectAssessments(true).pipe(
            map((assessments: AssessmentPage[]) => assessments.find(assessment => this.sanitizeAssessmentName(assessment.name) === this.sanitizeAssessmentName(name))),
        ));
    }

    isNameInInventory(name: string): boolean {
        const assessments = (<Array<AssessmentPage>>this.getAssessments(true)());
		return !!assessments?.find(entity => entity.name?.toLowerCase() === name?.toLowerCase());
    }

    sanitizeAssessmentName(name: string): string {
        if (!name) return '';
        
        // Remove HTML tags, normalize whitespace, convert to lowercase, and trim
        const decoded = this.sanitizer.sanitize(1, name); // 1 is the value for HTML
        return (decoded || name)
            .toLowerCase()
            .replace(/\s+/g, ' ') // Replace multiple whitespace with single space
            .trim();
    }

    getAssessmentsBySystem(templateTypes?: AssessmentTemplateEnum[], useCase: AssessmentUseCaseEnum = undefined): Observable<Map<AssessmentResourceId, AssessmentPage[]>> {
        return this.selectAssessments(true, useCase).pipe(
            map(assessments => {
                const map = new Map<AssessmentResourceId, AssessmentPage[]>();
                assessments.forEach(assessment => {
                    if (!templateTypes || templateTypes.includes(assessment.type as AssessmentTemplateEnum)) {
                        const systems = this.getRelatedEntitiesByAssessment(assessment, AssessmentQuestionTypeEnum.DataSources) as AssessmentResourceId[];
                        systems?.forEach(system => {
                            if (map.has(system)) {
                                map.set(system, [...map.get(system), assessment]);
                            } else {
                                map.set(system, [assessment]);
                            }
                        });
                    }
                });
                return map;
            })
        );
    }

    getAssessmentsBySystemId(systemId: string, useCase: AssessmentUseCaseEnum = undefined): Observable<AssessmentPage[]> {
        return this.getAssessmentsBySystem(undefined, useCase).pipe(
            map(assessmentsBySystem => assessmentsBySystem.get(systemId) || [])
        );
    }

    /**
     * The processing activity whose name matches `name`, or undefined when the tenant has none.
     *
     * Scoped to PA assessments: a business purpose IS a processing activity, so matching any assessment
     * type would link a purpose to an unrelated DPIA that happens to share a name. Compared through
     * `sanitizeAssessmentName` because assessment names are persisted HTML-escaped while the name being
     * looked up is plain text — a raw === would miss every name containing an apostrophe.
     *
     * TODO: served from the store for now; drop this once the agent contract carries the activity id.
     */
    selectProcessingActivityByName(name: string): Observable<AssessmentPage | undefined> {
        this.init();

        const sanitizedName = this.sanitizeAssessmentName(name);
        return this.selectAssessments(true, AssessmentUseCaseEnum.Ropa).pipe(
            map((assessments: AssessmentPage[]) => assessments.find(assessment =>
                assessment.type === AssessmentTemplateEnum.PA
                && this.sanitizeAssessmentName(assessment.name) === sanitizedName)),
        );
    }

    selectAssessmentsBySystemIds(systemIds: string[], useCase: AssessmentUseCaseEnum = undefined): Observable<AssessmentPage[]> {
        this.init();

        return this.getAssessmentsBySystem(undefined, useCase).pipe(
            map(assessmentsBySystem => {
                const assessments = systemIds.flatMap(systemId => assessmentsBySystem.get(systemId) ?? []);
                return [...new Map(assessments.map(assessment => [assessment.id, assessment])).values()];
            })
        );
    }
        
    /**
     * Base dataset for the rail counters, optionally narrowed to a use-case tab's label so each
     * counter reflects the current tab's "world" (e.g. Draft in the Vendor tab = Vendor drafts).
     * `baseLabel` is undefined on the All tab / off-flag → unscoped, as before.
     */
    private selectAssessmentsScopedByLabel(useCase: AssessmentUseCaseEnum = undefined, baseLabel?: string | null): Observable<AssessmentPage[]> {
        const base = (this.selectAssessments(true, useCase) as Observable<AssessmentPage[]>).pipe(
            // Processing Activities are hidden from the unified assessments table, so the rail counters must
            // exclude them too — otherwise "All" shows e.g. 99 while the table renders 81 (Figma review /
            // Oz). The RoPA page (useCase=Ropa) counts only PAs, so it is left untouched. No-op off-flag.
            map((assessments: AssessmentPage[]) => this.excludeProcessingActivitiesForCounts(assessments, useCase)),
        );
        if (!baseLabel) {
            return base;
        }
        // Resolve the label id per-emission (NOT once up front): the rail's count observables are
        // created during init, often before the labels store is populated, so an up-front lookup
        // would be undefined and the scope would silently no-op (global counts).
        return base.pipe(
            map((assessments: AssessmentPage[]) => {
                const labelId = this.aiAssessmentsLabelsService.getLabelByName(baseLabel)?.id;
                if (labelId === undefined) {
                    return assessments;
                }
                return assessments.filter(assessment => assessment?.tags?.some(tag => Number(tag.id) === Number(labelId)));
            }),
        );
    }

    /** Drop PA assessments from the unified rail counts (they live under Processing Activities and are hidden
     *  from the unified table). Kept on the RoPA page (useCase=Ropa). Mirrors the table's
     *  `excludeProcessingActivitiesWhenUnified`. */
    private excludeProcessingActivitiesForCounts(assessments: AssessmentPage[], useCase: AssessmentUseCaseEnum): AssessmentPage[] {
        if (useCase === AssessmentUseCaseEnum.Ropa) {
            return assessments ?? [];
        }
        return (assessments ?? []).filter(assessment => assessment?.type !== AssessmentTemplateEnum.PA);
    }

    selectCountByStatus(status?: AssessmentStatusEnum, useCase: AssessmentUseCaseEnum = undefined, baseLabel?: string | null): Observable<number> {
        return this.selectAssessmentsScopedByLabel(useCase, baseLabel).pipe(
            map((assessments: AssessmentPage[]) => {
                switch (status) {
                    case AssessmentStatusEnum.Draft:
                        return assessments.filter(assessment => assessment.state === AssessmentStatusEnum.Draft).length;
                    case AssessmentStatusEnum.Completed:
                        return assessments.filter(assessment => assessment.state === AssessmentStatusEnum.Completed).length;
                    case AssessmentStatusEnum.InProgress:
                        return assessments.filter(assessment => assessment.state === AssessmentStatusEnum.InProgress).length;
                    case AssessmentStatusEnum.Reject:
                        return assessments.filter(assessment => assessment.state === AssessmentStatusEnum.Reject).length;
                    default:
                        return assessments.length;
                }
            })
        );
    }

    selectCountByCollabState(state?: AssessmentCollaborationState, useCase: AssessmentUseCaseEnum = undefined): Observable<number> {
        return this.selectAssessments(true, useCase).pipe(
            map((assessments: AssessmentPage[]) => {
                switch (state) {
                    case AssessmentCollaborationState.Submitted:
                        return assessments.filter(assessment => assessment.collabState === AssessmentCollaborationState.Submitted).length;
                    case AssessmentCollaborationState.Shared:
                        return assessments.filter(assessment => assessment.collabState === AssessmentCollaborationState.Shared).length;
                    default:
                        return assessments.length;
                }
            })
        );
    }

    selectCountByHasCollabInvites(useCase: AssessmentUseCaseEnum = undefined): Observable<number> {
        return this.selectAssessments(true, useCase).pipe(
            map((assessments: AssessmentPage[]) =>
                assessments.filter(assessment =>
                    assessment.collabInvites?.some(
                        (invite) => !invite.isDone && invite.status !== AssessmentInviteeStatusEnum.Revoked,
                    )
                ).length
            )
        );
    }

    selectCountByCollabCompleted(useCase: AssessmentUseCaseEnum = undefined): Observable<number> {
        return this.selectAssessments(true, useCase).pipe(
            map((assessments: AssessmentPage[]) =>
                assessments.filter(assessment =>
                    assessment.collabInvites?.some((invite) => invite.isDone)
                ).length
            )
        );
    }

    selectCountByCollabOrCompleted(useCase: AssessmentUseCaseEnum = undefined, baseLabel?: string | null): Observable<number> {
        return this.selectAssessmentsScopedByLabel(useCase, baseLabel).pipe(
            map((assessments: AssessmentPage[]) =>
                assessments.filter(assessment =>
                    assessment.collabInvites?.some(
                        (invite) => invite.isDone || invite.status !== AssessmentInviteeStatusEnum.Revoked,
                    )
                ).length
            )
        );
    }

    revokeCollaborator(assessmentId: string, email: string): Observable<void> {
        return this.apiClientAssessmentService.revokeCollaborator(assessmentId, email).pipe(
            map(instance => this.mapInstanceOrigianlStateAndStore(instance)),
            map(() => void 0)
        );
    }

    inviteCollaborator(assessmentId: string, email: string, message: string, visibilityScope?: CollaboratorVisibilityScopeEnum): Observable<void> {
        return this.apiClientAssessmentService.inviteCollaborator(assessmentId, email, message, visibilityScope).pipe(
            map(instance => this.mapInstanceOrigianlStateAndStore(instance)),
            map(() => void 0)
        );
    }

    /** Section visibility (DevSectionVisibility): change an active collaborator's access scope. */
    updateCollaboratorScope(assessmentId: string, email: string, visibilityScope: CollaboratorVisibilityScopeEnum): Observable<void> {
        return this.apiClientAssessmentService.updateCollaboratorScope(assessmentId, email, visibilityScope).pipe(
            map(instance => this.mapInstanceOrigianlStateAndStore(instance)),
            map(() => void 0)
        );
    }

    selectCountByType(type: string, useCase: AssessmentUseCaseEnum = undefined): Observable<number> {
        return this.selectAssessments(true, useCase).pipe(
            map((assessments: AssessmentPage[]) => assessments.filter(assessment => this.filterAssesmentsByType(assessment.type, type))?.length ?? 0)
        );
    }

    selectCountByLabel(labelName: string, useCase: AssessmentUseCaseEnum = undefined, baseLabel?: string | null): Observable<number> {
        return this.selectAssessmentsScopedByLabel(useCase, baseLabel).pipe(
            // Resolve the label id INSIDE the map (per emission), not once up front: the rail counters are
            // wired at init, often before the labels store loads, so an up-front lookup would be undefined
            // and stay a stale 0. Mirrors selectAssessmentsScopedByLabel.
            map((assessments: AssessmentPage[]) => {
                const labelId = this.aiAssessmentsLabelsService.getLabelByName(labelName)?.id;
                return assessments.filter(assessment =>
                    assessment?.tags?.some(tag => tag?.id === labelId))?.length ?? 0;
            })
        );
    }

    selectCountByCreator(creatorName: string, useCase: AssessmentUseCaseEnum = undefined, baseLabel?: string | null): Observable<number> {
        return this.selectAssessmentsScopedByLabel(useCase, baseLabel).pipe(
            map((assessments: AssessmentPage[]) => assessments.filter(assessment =>
                this.agentIdToNamePipe.transform(assessment.agentId) === creatorName)?.length ?? 0)
        );
    }

    private filterAssesmentsByType(assessmentType: string, type: string): boolean {
        const cmsTypes = this.contentPipe.transform('ai-assessments.types');

        return assessmentType === type || cmsTypes[AssessmentTemplateEnum[assessmentType]] === type;
    }

    selectCountByCollabResponseStatus(
        category: AssessmentFilteringCategoryEnum.NoResponse | AssessmentFilteringCategoryEnum.PartialResponse | AssessmentFilteringCategoryEnum.ReadyForReview,
        useCase: AssessmentUseCaseEnum = undefined,
        baseLabel?: string | null,
    ): Observable<number> {
        return this.selectAssessmentsScopedByLabel(useCase, baseLabel).pipe(
            map((assessments: AssessmentPage[]) =>
                assessments.filter(assessment => this.matchesCollabResponseCategory(assessment, category)).length
            )
        );
    }

    private matchesCollabResponseCategory(
        assessment: AssessmentPage,
        category: AssessmentFilteringCategoryEnum.NoResponse | AssessmentFilteringCategoryEnum.PartialResponse | AssessmentFilteringCategoryEnum.ReadyForReview,
    ): boolean {
        const activeInvites = assessment.collabInvites?.filter(i => i.status !== AssessmentInviteeStatusEnum.Revoked) ?? [];
        const total = activeInvites.length;
        const doneCount = activeInvites.filter(i => i.isDone).length;
        const closedStatuses = [AssessmentStatusEnum.Completed, AssessmentStatusEnum.Reject];
        if (category === AssessmentFilteringCategoryEnum.NoResponse) {
            return total > 0 && doneCount === 0 && !closedStatuses.includes(assessment.state);
        }
        if (category === AssessmentFilteringCategoryEnum.PartialResponse) {
            return total > 0 && doneCount > 0 && doneCount < total && !closedStatuses.includes(assessment.state);
        }
        return total > 0 && doneCount === total;
    }

    selectCountByReviewDateIsDue(useCase: AssessmentUseCaseEnum = undefined, baseLabel?: string | null): Observable<number> {
        return this.selectAssessmentsScopedByLabel(useCase, baseLabel).pipe(
            map((assessments: AssessmentPage[]) => assessments.filter(assessment => this.isDateInPastPipe.transform(assessment.reviewDate)).length)
        );
    }

    selectCountByCustomView(customView: TableCustomView, useCase: AssessmentUseCaseEnum = undefined, baseLabel?: string | null): Observable<number> {
        const customViewFilters = this.updateFilters(customView.filters);
        // Scope the count to the active use-case tab so a saved view's sidebar number reflects the tab it
        // is combined with (e.g. "pikachu" under the Privacy tab counts only Privacy assessments).
        return this.selectAssessmentsScopedByLabel(useCase, baseLabel).pipe(
            map((assessments: AssessmentPage[]) => this.filterAssessments(assessments, customViewFilters || customView.filters)?.length ?? 0)
        );
	}
        
    selectUniqueDataSources(): Observable<SystemInstance[]> {
        return this.systemsQuery.selectLoading().pipe(
            filter(loading => !loading),
            switchMap(() => combineLatest([this.systemsQuery.selectAllSystems(), this.selectAssessments(true)])),
            map(([systems, assessments]) => this.getUniqueDataSourcesList(systems, assessments as AssessmentPage[])),
        );
    }

    selectUniqueDataSourcesWithRisks(): Observable<SystemInstance[]> {
        return this.systemsQuery.selectLoading().pipe(
            filter(loading => !loading),
            switchMap(() => combineLatest([this.systemsQuery.selectAllSystems(), this.selectAssessmentsWithRisks(true)])),
            map(([systems, assessments]) => this.getUniqueDataSourcesList(systems, assessments as AssessmentPage[])),
        );
    }

    selectUniqueAssessmentLabelsWithRisks(): Observable<AssessmentCustomLabel[]> {
        return combineLatest([
            this.selectAssessmentsWithRisks(true),
            this.aiAssessmentsLabelsService.selectLabels()
        ]).pipe(
            map(([assessments, labelsMap]) =>
                this.extractUniqueLabels(assessments as AssessmentPage[], labelsMap as Map<number, AssessmentCustomLabel>))
        );
    }
        
    private getUniqueDataSourcesList(systems: SystemInstance[], assessments: AssessmentPage[]): SystemInstance[] {
        return assessments.reduce((prev, curr) => {
            const vendorSpesificSystems = 
                curr.vendorSpecificId ? [systems.find(system => system.systemId === curr.vendorSpecificId)].filter(Boolean) : [];

            const instances = curr.sections
                ?.flatMap(section => section.questions)
                ?.filter(question => question.type === AssessmentQuestionTypeEnum.DataSources)
                ?.map(question => question.value as AssessmentEntity[])
                ?.flat()
                .filter(Boolean)
                .map(entity => systems.find(system => system.systemId === this.entityToId(entity)))
                .filter(Boolean) as SystemInstance[];

            return !!instances ? Array.from(new Set([...prev, ...instances, ...vendorSpesificSystems])) : prev;
        }, []);
    }

    selectUniqueDataRoles(): Observable<AssessmentResourceId[]> {
        return this.selectCustomFieldsLoading().pipe(
            filter(loading => !loading),
            switchMap(() => this.selectAssessments(true)),
            map(assessments => this.getUniqueEntityList(AssessmentQuestionTypeEnum.DataRole, assessments as AssessmentPage[]) as AssessmentResourceId[]),
        );
    }

    selectUniqueDataSubjects(): Observable<AssessmentEntity[]> {
        return this.selectCustomFieldsLoading().pipe(
            filter(loading => !loading),
            switchMap(() => this.selectAssessments(true)),
            map(assessments => this.getUniqueEntityList(AssessmentQuestionTypeEnum.DataSubjects, assessments as AssessmentPage[]) as AssessmentEntity[]),
        );
    }

    selectUniqueBusinessUnits(): Observable<AssessmentEntity[]> {
        return this.selectCustomFieldsLoading().pipe(
            filter(loading => !loading),
            switchMap(() => this.selectAssessments(true)),
            map(assessments => this.getUniqueEntityList(AssessmentQuestionTypeEnum.BusinessUnit, assessments as AssessmentPage[]) as AssessmentEntity[]),
        );
    }

    selectUniqueBusinessOwners(): Observable<AssessmentResourceId[]> {
        return this.selectCustomFieldsLoading().pipe(
            filter(loading => !loading),
            switchMap(() => this.selectAssessments(true)),
            map(assessments => this.getUniqueEntityList(AssessmentQuestionTypeEnum.BusinessOwner, assessments as AssessmentPage[]) as AssessmentResourceId[]),
        );
    }

    selectUniqueOwners(): Observable<string[]> {
        return this.selectAssessments(true).pipe(
            filter((assessments): assessments is AssessmentPage[] => Array.isArray(assessments)),
            map(assessments => Array.from(new Set(assessments.map(a => a.ownerId).filter((id): id is string => !!id)))),
        );
    }

    selectUniqueCreators(): Observable<ProfileAgent[]> {
        return combineLatest([this.profileQuery.selectAgents(), this.selectAssessments(true)]).pipe(
            map(([users, assessments]) => this.getUniqueCreatorsList(users, assessments as AssessmentPage[])),
        );
    }

    selectUniqueAiAgentsCreators(useCase: AssessmentUseCaseEnum = null): Observable<Partial<ProfileAgent>[]> {
        return this.selectAssessments(true, useCase).pipe(   
            map((assessments: AssessmentPage[]) => {
                const uniqueCreators = new Set<string>();
                assessments.forEach(assessment => {
                    if (assessment?.createdByAiContext?.miraId) {
                        uniqueCreators.add(assessment.agentId);
                    }
                });
                return Array.from(uniqueCreators).map(creator => ({
                    id: creator,
                    name: this.agentIdToNamePipe.transform(creator)
                }));
            })
        );
    }
    
    private getUniqueCreatorsList(users: ProfileAgent[], assessments: AssessmentPage[]): ProfileAgent[] {
        return users.filter(user => assessments.some(assessment => assessment.agentId === user.id));
    }

    private getUniqueEntityList(valueType: AssessmentQuestionTypeEnum, assessments: AssessmentPage[]): AssessmentEntity[] | AssessmentResourceId[] {
        const entityMap = new Map<AssessmentResourceId, AssessmentEntity | AssessmentResourceId>();

		let instances: AssessmentEntity[] | AssessmentResourceId[] = [];
        assessments.forEach(assessment => {
            instances = assessment.sections
                ?.flatMap(section => section.questions)
                ?.filter(question => question.type === valueType)
                ?.map(question => question.value as AssessmentEntity[])
                ?.flat()
                .filter(Boolean);

			if (valueType === AssessmentQuestionTypeEnum.BusinessOwner) {
				instances = assessment.ownerId ? [assessment.ownerId] : [];
			}
    
            instances?.forEach((entity: AssessmentEntity | AssessmentResourceId) => {
                if ([
                    AssessmentQuestionTypeEnum.BusinessOwner, 
                    AssessmentQuestionTypeEnum.BusinessPurpose, 
                    AssessmentQuestionTypeEnum.DataSources,
                    AssessmentQuestionTypeEnum.DataRole,
                    AssessmentQuestionTypeEnum.LinkedAssessment,
                ].includes(valueType)) {
                    const id = entity as AssessmentResourceId;
                    entityMap.set(id, id);
                } else {
                    const ent = entity as AssessmentEntity;
                    entityMap.set(ent.id, ent);
                }
            });
        });
    
        return Array.from(entityMap.values()).filter(Boolean) as AssessmentEntity[] | AssessmentResourceId[];
    }

    searchAssessments(
        assessments: AssessmentPage[],
        search: string,
        enrichments?: AssessmentSearchEnrichments,
    ): AssessmentPage[] {
        if (!assessments || !Array.isArray(assessments)) return [];
        if (!isQuerySearchable(search)) return assessments;
        const hasEnrichments = !!enrichments;
        const statuses = this.contentPipe.transform('ai-assessments.statuses') as DropdownOption[] | undefined;
        const typesContent = this.contentPipe.transform('ai-assessments.types') as Record<string, string> | undefined;
        const reuseCache = !hasEnrichments
            && this.cachedFuse?.source === assessments
            && this.cachedFuse?.statusesRef === statuses
            && this.cachedFuse?.typesRef === typesContent;
        let fuse: Fuse<FuseDocItem<AssessmentPage>>;
        let keys: string[];
        if (reuseCache && this.cachedFuse) {
            ({ fuse, keys } = this.cachedFuse);
        } else {
            const statusDisplayMap = this.buildStatusDisplayMap(statuses);
            const items: FuseDocItem<AssessmentPage>[] = assessments.map(assessment => {
                const cached = this.getSearchData(assessment);
                const extras = enrichments?.getExtras(assessment);
                const dynamicParts: SearchExtra[] = [];
                if (extras?.length) dynamicParts.push(...extras);
                if (assessment.state) {
                    const display = statusDisplayMap.get(assessment.state);
                    if (display && display !== assessment.state) {
                        dynamicParts.push({ label: 'statusDisplay', value: display });
                    }
                }
                if (assessment.type) {
                    const typeDisplay = this.lookupTypeDisplay(assessment.type, typesContent);
                    if (typeDisplay && typeDisplay !== assessment.type) {
                        dynamicParts.push({ label: 'typeDisplay', value: typeDisplay });
                    }
                }
                const doc = dynamicParts.length
                    ? buildDocFromParts([...cached.parts, ...dynamicParts])
                    : cached.doc;
                return { item: assessment, doc };
            });
            ({ fuse, keys } = createFuse(items));
            if (!hasEnrichments) {
                this.cachedFuse = { source: assessments, statusesRef: statuses, typesRef: typesContent, fuse, keys };
            }
        }
        const logicalQuery = buildAndQuery(search, keys);
        if (!logicalQuery) return assessments;
        const results = fuse.search(logicalQuery);
        const debug = this.isSearchDebugEnabled();
        return results.map(r => {
            if (debug) {
                this.logSearchMatch(r.item.item, r.matches, r.score);
            }
            return r.item.item;
        });
    }

    private isSearchDebugEnabled(): boolean {
        try {
            return localStorage.getItem(SEARCH_DEBUG_KEY) === 'true';
        } catch {
            return false;
        }
    }

    private logSearchMatch(
        assessment: AssessmentPage,
        matches: readonly FuseResultMatch[] | undefined,
        score: number | undefined,
    ): void {
        const infos = extractMatchInfos(matches);
        const label = assessment.name ? `${assessment.name} (#${assessment.id})` : `#${assessment.id}`;
        /* eslint-disable no-console */
        console.groupCollapsed(`%c[assessment-search] %c✓ ${label}`, 'color: #888;', 'color: #2eb886; font-weight: bold;');
        if (score !== undefined) {
            console.log('fuse score:', score);
        }
        if (infos.length) {
            for (const info of infos) {
                console.log(
                    `%cfield "${info.path}":%c ${JSON.stringify(info.value)}`,
                    'color: #d97706; font-weight: bold;',
                    'color: #0ea5e9;',
                );
            }
        } else {
            console.log('matched (no field attribution)');
        }
        console.groupEnd();
        /* eslint-enable no-console */
    }

    /**
     * Mirror of AiAssessmentTemplateNamePipe.transform — look up the user-facing
     * label for an assessment type from the ai-assessments.types CMS map.
     * Handles both raw enum values and template-key inputs.
     */
    private lookupTypeDisplay(type: string, typesContent: Record<string, string> | undefined): string | undefined {
        if (!type || !typesContent) return undefined;
        // The CMS map is keyed by AssessmentTemplateEnum *value*. assessment.type
        // is typically already a value; fall back to a case-insensitive scan.
        if (typesContent[type]) return typesContent[type];
        const lower = type.toLowerCase();
        for (const [k, v] of Object.entries(typesContent)) {
            if (k.toLowerCase() === lower) return v;
        }
        return undefined;
    }

    private buildStatusDisplayMap(statuses: DropdownOption[] | undefined): Map<string, string> {
        const map = new Map<string, string>();
        if (!statuses) return map;
        for (const s of statuses) {
            if (s?.id != null && s?.value != null) {
                map.set(String(s.id), String(s.value));
            }
        }
        return map;
    }

    private getSearchData(assessment: AssessmentPage): { parts: readonly SearchExtra[]; doc: SearchableDoc } {
        let cached = this.searchDocCache.get(assessment);
        if (!cached) {
            const parts = this.buildAssessmentSearchParts(assessment);
            cached = { parts, doc: buildDocFromParts(parts) };
            this.searchDocCache.set(assessment, cached);
        }
        return cached;
    }

    private buildAssessmentSearchParts(assessment: AssessmentPage): SearchExtra[] {
        const parts: SearchExtra[] = [];

        if (assessment.name) parts.push({ label: 'name', value: assessment.name });
        if (assessment.type) parts.push({ label: 'type', value: assessment.type });
        if (assessment.state) {
            parts.push({ label: 'status', value: assessment.state });
        }

        assessment.tags?.forEach(tag => {
            if (tag?.name) parts.push({ label: 'labels', value: tag.name });
        });

        assessment.collabInvites?.forEach(invite => {
            if (invite?.email) parts.push({ label: 'collaboratorsEmail', value: invite.email });
            if (invite?.inviterEmail) parts.push({ label: 'collaboratorsInviterEmail', value: invite.inviterEmail });
        });

        if (assessment.collabState) {
            parts.push({ label: 'collaborationStatus', value: String(assessment.collabState) });
        }

        const done = assessment.collaboratorsDoneCount;
        const total = assessment.collaboratorsTotalCount;
        if (done !== undefined && total !== undefined) {
            parts.push({ label: 'collabCompleted', value: `${done}/${total}` });
        }

        if (assessment.reviewDate) parts.push({ label: 'reviewDate', value: assessment.reviewDate });
        if (assessment.createdDate) parts.push({ label: 'createdDate', value: assessment.createdDate });
        if (assessment.updatedDate) parts.push({ label: 'lastUpdate', value: assessment.updatedDate });
        if (assessment.stateUpdatedAt) parts.push({ label: 'statusChangeDate', value: assessment.stateUpdatedAt });

        this.appendRelatedEntityParts(assessment, AssessmentQuestionTypeEnum.BusinessUnit, 'businessUnit', parts);
        this.appendRelatedEntityParts(assessment, AssessmentQuestionTypeEnum.DataSources, 'dataSources', parts);
        this.appendRelatedEntityParts(assessment, AssessmentQuestionTypeEnum.DataSubjects, 'dataSubjects', parts);

        return parts;
    }

    private appendRelatedEntityParts(
        assessment: AssessmentPage,
        questionType: AssessmentQuestionTypeEnum,
        columnLabel: string,
        out: SearchExtra[],
    ): void {
        let related: unknown;
        try {
            related = this.getRelatedEntitiesByAssessment(assessment, questionType);
        } catch {
            return;
        }
        const arr = Array.isArray(related) ? related : related ? [related] : [];
        arr.forEach(entity => {
            if (entity == null) return;
            if (typeof entity === 'string' || typeof entity === 'number') {
                out.push({ label: columnLabel, value: String(entity) });
                return;
            }
            if (typeof entity === 'object') {
                const e = entity as Record<string, unknown>;
                if (e.id) out.push({ label: `${columnLabel}Id`, value: String(e.id) });
                if (e.name) out.push({ label: `${columnLabel}Name`, value: String(e.name) });
                if (e.label) out.push({ label: `${columnLabel}Label`, value: String(e.label) });
            }
        });
    }

    /**
     * Drop the stored cache entry for `assessment` and immediately recompute
     * a fresh one. Use this whenever an assessment is mutated so the WeakMap
     * always holds the current snapshot for any subsequent search.
     */
    private refreshSearchDoc(assessment: AssessmentPage | null | undefined): void {
        if (!assessment) {
            return;
        }
        this.searchDocCache.delete(assessment);
        const parts = this.buildAssessmentSearchParts(assessment);
        this.searchDocCache.set(assessment, { parts, doc: buildDocFromParts(parts) });
        this.cachedFuse = null;
    }

    /** Drop the cache entry without rebuilding (use when the ref is being orphaned). */
    private evictSearchDoc(assessment: AssessmentPage | null | undefined): void {
        if (assessment) {
            this.searchDocCache.delete(assessment);
            this.cachedFuse = null;
        }
    }
    
    private isCustomFieldFilter(id: string | undefined): boolean {
        if (!id) return false;
        const field = this.customFieldsV2Service.getField(id);
        return !!field && !field.isPredefined;
    }

    filterAssessments(assessments: AssessmentPage[], filters: FilterCategory[]): AssessmentPage[] {
        if (!assessments || !Array.isArray(assessments)) return [];
        if (!filters) return assessments;
        for (let filter of filters) {
            if (filter?.options?.some(option => option?.selected)) {
                if (filter?.id === AssessmentFilteringGroupEnum.Type) {
                    assessments = this.filterAssessmentsByType(assessments, filter);
                }
                if (filter?.id === AssessmentFilteringGroupEnum.Labels) {
                    assessments = this.filterAssessmentsByLabel(assessments, filter);
                }
                if (filter?.id === AssessmentFilteringGroupEnum.Collaborators) {
                    assessments = this.filterAssessmentsByCollabState(assessments, filter);
                }
                if (filter?.id === AssessmentFilteringGroupEnum.HasCollabInvites) {
                    assessments = assessments.filter(assessment => assessment.collabInvites?.length > 0);
                }
                if (filter?.id === AssessmentFilteringGroupEnum.Status) {
                    assessments = this.filterAssessmentsByProperty(assessments, filter, 'state');
                }
                if (filter?.id === AssessmentFilteringGroupEnum.DataSource) {
                    assessments = this.filterAssessmentsByEntityType(assessments, filter, AssessmentQuestionTypeEnum.DataSources);
                }
                if (filter?.id === AssessmentFilteringGroupEnum.ReviewDate) {
                    assessments = (assessments as AssessmentPage[]).filter(assessment => this.isDateInPastPipe.transform(assessment.reviewDate));
                }
                if (filter?.id === AssessmentFilteringGroupEnum.DataRole) {
                    assessments = this.filterAssessmentsByEntityType(assessments, filter, AssessmentQuestionTypeEnum.DataRole);
                }
                if (filter?.id === AssessmentFilteringGroupEnum.DataSubject) {
                    assessments = this.filterAssessmentsByEntityType(assessments, filter, AssessmentQuestionTypeEnum.DataSubjects);
                }
                if (filter?.id === AssessmentFilteringGroupEnum.BusinessUnit) {
                    assessments = this.filterAssessmentsByEntityType(assessments, filter, AssessmentQuestionTypeEnum.BusinessUnit);
                }
                if (filter?.id === AssessmentFilteringGroupEnum.BusinessOwner) {
                    assessments = this.filterAssessmentsByEntityType(assessments, filter, AssessmentQuestionTypeEnum.BusinessOwner);
                }
				if (filter?.id === AssessmentFilteringGroupEnum.Owner) {
                    assessments = this.filterAssessmentsByOwner(assessments, filter);
                }
                if (filter?.id === AssessmentFilteringGroupEnum.Creator) {
                    assessments = this.filterAssessmentsByCreator(assessments, filter);
                }
                if (this.isCustomFieldFilter(filter?.id)) {
                    assessments = this.filterAssessmentsByCustomField(assessments, filter);
                }
            }
        }
        return assessments;
    }

    private filterAssessmentsByProperty<T extends keyof AssessmentPage>(assessments: AssessmentPage[], filter: FilterCategory, property: T): AssessmentPage[] {
        return assessments.filter(
            assessment => filter.options
                .filter(option => option?.selected)
                .map(option => option?.id)
                .includes(assessment[property as string])
        );
    }

    private filterAssessmentsByType(assessments: AssessmentPage[], filter: FilterCategory): AssessmentPage[] {
        const cmsTypes = this.contentPipe.transform('ai-assessments.types');
        const optionsIds = filter.options
            .filter(option => option?.selected)
            .map(option => option?.id);

        return assessments.filter(
            assessment => optionsIds.includes(assessment.type) || optionsIds.includes(cmsTypes[AssessmentTemplateEnum[assessment.type]])
        );
    }

    private filterAssessmentsByLabel(
        assessments: AssessmentPage[],
        filter: FilterCategory
    ): AssessmentPage[] {
        const selectedNames = filter.options.filter(option => option?.selected).map(option => option?.id);
        const toId = (name: string) => this.aiAssessmentsLabelsService.getLabelByName(name)?.id;

        // The built-in use-case labels (Privacy/Vendor/AI) act as the tab's hard "world" scope, so they
        // are ANDed, while regular labels stay ORed among themselves. This makes "Vendor world + DPIA"
        // resolve to Vendor AND DPIA rather than Vendor OR DPIA.
        const defaults = this.getDefaultLabelNames();
        const baseIds = selectedNames.filter(name => defaults.has(name)).map(toId).filter((id): id is number => id !== undefined);
        const regularIds = selectedNames.filter(name => !defaults.has(name)).map(toId).filter((id): id is number => id !== undefined);

        return assessments.filter(assessment => {
            const tagIds = assessment.tags?.map(tag => Number(tag.id)) ?? [];
            const matchesBase = baseIds.every(id => tagIds.includes(id));
            const matchesRegular = regularIds.length === 0 || regularIds.some(id => tagIds.includes(id));
            return matchesBase && matchesRegular;
        });
    }

    private filterAssessmentsByCollabState(assessments: AssessmentPage[], filter: FilterCategory): AssessmentPage[] {
        const optionsIds = filter.options
            .filter(option => option?.selected)
            .map(option => option?.id);

        const hasCollabCompleted = optionsIds.includes(AssessmentFilteringCategoryEnum.CollabCompleted);
        const hasShared = optionsIds.includes(AssessmentCollaborationState.Shared);
        const hasNoResponse = optionsIds.includes(AssessmentFilteringCategoryEnum.NoResponse);
        const hasPartialResponse = optionsIds.includes(AssessmentFilteringCategoryEnum.PartialResponse);
        const hasReadyForReview = optionsIds.includes(AssessmentFilteringCategoryEnum.ReadyForReview);

        return assessments.filter(assessment => {
            if (hasCollabCompleted || hasShared) {
                const matchesCollabCompleted =
                    hasCollabCompleted && assessment.collabInvites?.some((invite) => invite.isDone);
                const matchesShared =
                    hasShared &&
                    assessment.collabInvites?.some(
                        (invite) => !invite.isDone && invite.status !== AssessmentInviteeStatusEnum.Revoked,
                    );
                return matchesCollabCompleted || matchesShared;
            }

            if (hasNoResponse || hasPartialResponse || hasReadyForReview) {
                if (hasNoResponse && this.matchesCollabResponseCategory(assessment, AssessmentFilteringCategoryEnum.NoResponse)) { return true; }
                if (hasPartialResponse && this.matchesCollabResponseCategory(assessment, AssessmentFilteringCategoryEnum.PartialResponse)) { return true; }
                if (hasReadyForReview && this.matchesCollabResponseCategory(assessment, AssessmentFilteringCategoryEnum.ReadyForReview)) { return true; }
                return false;
            }

            return optionsIds.includes(assessment.collabState);
        });
    }
    
    private filterAssessmentsByEntityType(assessments: AssessmentPage[], filter: FilterCategory, type: AssessmentQuestionTypeEnum): AssessmentPage[] {
		return (assessments as AssessmentPage[]).filter(assessment => {
            const selectedOptions = filter.options.filter(option => option?.selected);
            const optionIds = selectedOptions.map(option => option?.id);
            const allQuestions = assessment.sections?.flatMap(section => section.questions) ?? [];
            const filteredQuestions = allQuestions.filter(question => question.type === type);
            const questionValues = filteredQuestions.map(question => question.value as AssessmentEntity[]);
            const flatEntities = questionValues.flat().filter(Boolean);

			if (type === AssessmentQuestionTypeEnum.DataSources) {
				const vendorSpecificId = assessment.vendorSpecificId as unknown as AssessmentEntity;
				if (vendorSpecificId) {
					flatEntities.push(vendorSpecificId);
				}
			}

            const entityIds = flatEntities.map(entity => this.entityToId(entity));
            const hasMatch = optionIds.some(item => entityIds.includes(item));
            return hasMatch;
        });
    }

	private filterAssessmentsByOwner(assessments: AssessmentPage[], filter: FilterCategory): AssessmentPage[] {
		const optionsIds = filter.options
			.filter(option => option?.selected)
			.map(option => option?.id);
		return assessments.filter(assessment => optionsIds.includes(assessment.ownerId));
	}

    private filterAssessmentsByCreator(assessments: AssessmentPage[], filter: FilterCategory): AssessmentPage[] {
		const optionsIds = filter.options
			.filter(option => option?.selected)
			.map(option => option?.id);
		return assessments.filter(assessment => optionsIds.includes(this.agentIdToNamePipe.transform(assessment.agentId)));
	}

    private filterAssessmentsByCustomField(assessments: AssessmentPage[], filter: FilterCategory): AssessmentPage[] {
        const selectedOptions = filter.options.filter(option => option?.selected);
        const optionIds = selectedOptions.map(option => option?.id);

        return (assessments as AssessmentPage[]).filter(assessment => {
            const allQuestions = assessment.sections?.flatMap(section => section.questions) ?? [];
            const questionValues = allQuestions.map(question => question.value as AssessmentEntity[]);
            const flatEntities = questionValues.flat().filter(Boolean);
            const entityIds = flatEntities.map(entity => this.entityToId(entity));
            return optionIds.some(item => entityIds.some(id => Array.isArray(id) ? id.includes(item) : id == item));
        });
    }

	private entityToId(entity: AssessmentEntity | AssessmentResourceId): string | string[] {
        if (entity.hasOwnProperty('customFieldId')) {
            if (entity.hasOwnProperty('textValue')) {
                return (entity as any).textValue?.toString();
            }
            if (entity.hasOwnProperty('options')) {
                return (entity as any).options?.filter(option => option.isSelected)?.map(option => option.id?.toString()) as string[];
            }
            return (entity as any).customFieldId?.toString();
        }
        if (entity.hasOwnProperty('id')) {
            return (entity as AssessmentEntity).id?.toString();
        }
        return entity as string;
	}
    
    sortAssessments(assessments: AssessmentPage[], sort: MineSort): AssessmentPage[] {
        if (!assessments || !Array.isArray(assessments)) return [];
        switch (sort?.active) {
            case AssessmentColumnEnum.Name:
                return assessments.sort((a, b) => sort.direction === SortDirectionEnum.Asc ?
                    a.name?.localeCompare(b.name) : b.name?.localeCompare(a.name)
                );
        
            case AssessmentColumnEnum.Type:
                return assessments.sort((a, b) => sort.direction === SortDirectionEnum.Asc ?
                    a.type?.localeCompare(b.type) : b.type?.localeCompare(a.type)
                );
        
            case AssessmentColumnEnum.Status:
                return assessments.sort((a, b) => sort.direction === SortDirectionEnum.Asc ?
                    this.sortByStatus(a, b) : this.sortByStatus(b, a)
                );
        
            case AssessmentColumnEnum.ReviewDate:
                return assessments.sort((a, b) => sort.direction === SortDirectionEnum.Asc ?
                    this.sortByReviewDate(a, b) : this.sortByReviewDate(b, a)
                );

            case AssessmentColumnEnum.CreatedDate:
                return assessments.sort((a, b) => sort.direction === SortDirectionEnum.Asc ?
                    this.sortByDate(a.createdDate, b.createdDate) : this.sortByDate(b.createdDate, a.createdDate)
                );

            case AssessmentColumnEnum.LastUpdate:
                return assessments.sort((a, b) => sort.direction === SortDirectionEnum.Asc ?
                    this.sortByDate(a.updatedDate, b.updatedDate) : this.sortByDate(b.updatedDate, a.updatedDate)
                );

            case AssessmentColumnEnum.StatusChangeDate:
                return assessments.sort((a, b) => sort.direction === SortDirectionEnum.Asc ?
                    this.sortByDate(a.stateUpdatedAt, b.stateUpdatedAt) : this.sortByDate(b.stateUpdatedAt, a.stateUpdatedAt)
                );

          default:
            return assessments;
        }
    }

    private sortByStatus(a: AssessmentPage, b: AssessmentPage): number {
        const aStatus = this.getAssessmentTableStatus(a);
        const bStatus = this.getAssessmentTableStatus(b);

        return `${aStatus.status.value}-${new Date(a.updatedDate ?? a.createdDate).getTime()}`
            .localeCompare(`${bStatus.status.value}-${new Date(b.updatedDate ?? b.createdDate).getTime()}`);
    }

    private getAssessmentTableStatus(instance: AssessmentPage): AssessmentTableStatus {
        const statuses = this.contentPipe.transform('ai-assessments.statuses') as DropdownOption[];

        return {
            date: this.datePipe.transform(instance.updatedDate ?? instance.createdDate, 'MMM y'),
            status: statuses.find(status => status?.id === instance.state)
        } as AssessmentTableStatus;
    }

    private sortByDate(a: string | undefined, b: string | undefined): number {
        return new Date(a ?? 0).valueOf() - new Date(b ?? 0).valueOf();
    }

    private sortByReviewDate(a: AssessmentPage, b: AssessmentPage): number {
        return new Date(a.reviewDate || '0').valueOf() - new Date(b.reviewDate || '0').valueOf();
    }

    /** Built-in use-case label names (Privacy / Vendor / AI) — passthrough to the labels service. */
    getDefaultLabelNames(): Set<string> {
        return this.aiAssessmentsLabelsService.getDefaultLabelNames();
    }

    getMineOSTemplates(useCase: AssessmentUseCaseEnum = undefined): AssessmentsMineOSTemplateResponse[] {
        if (!useCase) {
            return this.FORM_TEMPLATES;
        }
        
        return this.FORM_TEMPLATES.filter(template =>
            this.assessmentsSplitByUseCaseService.isTemplateInUseCase(
                useCase,
                template.type as AssessmentTemplateEnum,
                template.tags
            )
        );
    }

    getMineOSTemplateByType(type: AssessmentTemplateEnum): AssessmentsMineOSTemplateResponse {
        return this.FORM_TEMPLATES.find(template => template.type === type);
    }
        
    getMineOSTemplate(type: string, name: string): AssessmentsMineOSTemplateResponse {
        return this.FORM_TEMPLATES.find(template => template.name === name && (template.type === type || template.type === AssessmentTemplateEnum[type])); 
    }
    
    getTemplates(useCase: AssessmentUseCaseEnum = undefined): Signal<Map<AssessmentResourceId, AssessmentsTemplate>> {
        return computed(() => {
            const templates = this.store.signal('templates')();
            if (templates instanceof Map) {
                if (useCase) {
                    return templates?.get(useCase);
                }

                // Flatten all inner maps from the parent templates map into a single map
                return Array.from((templates as Map<AssessmentUseCaseEnum, Map<AssessmentResourceId, AssessmentsTemplate>>)?.values() ?? []).reduce((acc, map) => {
                    if (map instanceof Map) {
                        map.forEach((value, key) => acc.set(key, value));
                    }
                    return acc;
                }, new Map<AssessmentResourceId, AssessmentsTemplate>());
            }
            return new Map<AssessmentResourceId, AssessmentsTemplate>();
        });
    }

    getTemplateById(id: AssessmentResourceId): AssessmentsTemplate {
        const templatesMap = this.getTemplates()();
        for (const template of templatesMap.values()) {
            if (template.id === id) {
                return template;
            }
        }
        return undefined;
    }
    
    getTemplate(type: AssessmentTemplateEnum, name: string): AssessmentsTemplate {
        return Array.from([...this.store.get('templates').values()])
            .flatMap(template => Array.from(template.values())).find(template => template.type === type && name === template.name);
    }

    getTemplateInstance(type: AssessmentTemplateEnum, name: string): AssessmentsMineOSTemplateResponse | AssessmentsTemplate {
        return this.getMineOSTemplate(type, name) ?? this.getTemplate(type, name) ?? this.getTemplateById(type as string);
    }

    selectTemplates(useCase: AssessmentUseCaseEnum = undefined): Observable<Map<AssessmentResourceId, AssessmentsTemplate>> {
        if (useCase) {
            return this.store.select('templates').pipe(
                map(templates => templates.get(useCase) as Map<AssessmentResourceId, AssessmentsTemplate>)
            );
        }

        return this.store.select('templates').pipe(
            map(templates => Array.from((templates as Map<AssessmentUseCaseEnum, Map<AssessmentResourceId, AssessmentsTemplate>>)?.values() ?? []).reduce((acc, map) => {
                if (map instanceof Map) {
                    map.forEach((value, key) => acc.set(key, value));
                }
                return acc;
            }, new Map<AssessmentResourceId, AssessmentsTemplate>()))
        );
    }

    selectTemplate(templateId: AssessmentResourceId): Observable<AssessmentsTemplate> {
        return this.selectTemplates().pipe(
            map(templates => templates.get(templateId))
        );
    }

    selectAssessmentTemplate(type: AssessmentTemplateEnum, name?: string): Observable<AssessmentsTemplate | undefined> {
        return this.selectTemplates().pipe(
          map((templates: Map<string, AssessmentsTemplate>) => 
            Array.from(templates.values()).find(template => {
                if (!!name) return template.type === type && template.name === name;
                return template.type === type;
            })
          )
        );
     }

    selectPaTemplate(): Observable<AssessmentsTemplate | undefined> {        
        return this.selectAssessmentTemplate(AssessmentTemplateEnum.PA);
    }

    selectAssessmentTypes(useCase: AssessmentUseCaseEnum = undefined): Observable<Array<string>> {
        const cmsTypes = this.contentPipe.transform('ai-assessments.types');
        return this.selectAssessments(true, useCase).pipe(
            map((assessments: AssessmentPage[]) => Array.from(new Set(assessments.map(assessment => assessment.type)))),
            map(types => types.reduce((acc, curr) => {
                if (!acc.some(item => cmsTypes[AssessmentTemplateEnum[item]] === cmsTypes[AssessmentTemplateEnum[curr]])) {
                    acc.push(curr);
                }
                return acc;
            }, [])),
        );
    }

    /**
     * All label names from the labels store, independent of whether any assessment currently uses
     * them. Unlike `selectAssessmentLabels` (assessment-derived, so empty until assessments load),
     * this reflects the seeded/built-in labels — used to seed the per-label dynamic views
     * so the use-case tabs (Privacy/Vendor/AI) filter reliably.
     * Emits once, only after the labels store finished loading (the store's initial value is an
     * empty Map, so taking the first raw emission would yield `[]` before the labels arrive).
     */
    selectAllLabelNames(): Observable<string[]> {
        return combineLatest([
            this.aiAssessmentsLabelsService.selectLabels(true) as Observable<AssessmentCustomLabel[]>,
            this.aiAssessmentsLabelsService.selectLoading(),
        ]).pipe(
            filter(([, loading]) => !loading),
            map(([labels]) => labels?.map(label => label.name) ?? []),
            first(),
        );
    }

    selectAssessmentLabels(useCase: AssessmentUseCaseEnum = undefined): Observable<string[]> {
        return combineLatest([
            this.selectAssessments(true, useCase),
            this.aiAssessmentsLabelsService.selectLabels()
        ]).pipe(
            map(([assessments, labelsMap]) => this.extractUniqueLabelNames(assessments as AssessmentPage[], labelsMap as Map<number, AssessmentCustomLabel>))
        );
    }
    
    private extractUniqueLabelNames(
        assessments: AssessmentPage[],
        labelsMap: Map<number, AssessmentCustomLabel>
    ): string[] {
        const uniqueLabelNames = new Set<string>();
    
        assessments
            .flatMap(assessment => assessment.tags ?? [])
            .forEach(tag => {
                const label = labelsMap.get(Number(tag?.id));
                if (label && !uniqueLabelNames.has(label.name)) {
                    uniqueLabelNames.add(label.name);
                }
            });

        return Array.from(uniqueLabelNames);
    }

    /**
     * Unique labels (id + name) that are actually attached to assessments carrying risks. Mirrors
     * `extractUniqueLabelNames` but keeps the label id so the risk-registry filter can send tag ids to
     * the BE (`GET /api/assessment/v4/risk?Relations.Tags=<ids>`) rather than names.
     */
    private extractUniqueLabels(
        assessments: AssessmentPage[],
        labelsMap: Map<number, AssessmentCustomLabel>
    ): AssessmentCustomLabel[] {
        const uniqueLabels = new Map<AssessmentResourceId, AssessmentCustomLabel>();

        assessments
            .flatMap(assessment => assessment.tags ?? [])
            .forEach(tag => {
                const label = labelsMap.get(Number(tag?.id));
                if (label && !uniqueLabels.has(label.id)) {
                    uniqueLabels.set(label.id, label);
                }
            });

        return Array.from(uniqueLabels.values());
    }

    getAiAssessmentTemplateEnumKeyByValue(value: AssessmentTemplateEnum): string | undefined {
        const keys = Object.keys(AssessmentTemplateEnum);
        const values = Object.values(AssessmentTemplateEnum);
        const findByKey = keys.find(key => key?.toLowerCase() === value?.toLowerCase() || AssessmentTemplateEnum[key]?.toLowerCase() === value?.toLowerCase());
        const findByValue = values.find(v => v?.toLowerCase() === value?.toLowerCase());
        return findByKey ?? findByValue;
    }

    getActions(): RxActions<AssessmentActions, {}> {
        return this.store.actions;
    }

    jumpToFormSection(formControlId: AssessmentResourceId): void {
        this.store.actions.jumpToFormSection(formControlId);
    }

    cloneFormRequest(request: AssessmentPage): void {
        this.store.actions.cloneFormRequest(request);
    }

    submitFormRequest(request: AssessmentPage): void {
        this.store.actions.submitFormRequest(request);
    }

    submitFormRequestFinished(state?: SubmissionState): void {
        this.store.actions.submitFormRequestFinished(state);
    }

    submitFormRequestCanceled(): void {
        this.store.actions.submitFormRequestCanceled();
    }

    markInstanceAsApproved(instanceId: AssessmentResourceId): void {
        this.store.actions.markInstanceAsApproved(instanceId);
    }

    approveInstance(instanceId: AssessmentResourceId): void {
        this.store.actions.approveInstance(instanceId);
    }
        
    updateInstanceName(instance: AssessmentPage, newName: string): void {
        this.store.actions.updateInstanceName({instance, newName});
    }

    updateInstanceReviewDate(instance: AssessmentPage, reviewDate?: string, ownerId?: string, callback?: Function): void {
        this.store.actions.updateInstanceReviewDate({instance, reviewDate, ownerId, callback});
    }

    deleteFormRequest(id: AssessmentResourceId): void {
        this.store.actions.deleteInstance(id);
    }

    selectInstance(instanceId: AssessmentResourceId): Observable<AssessmentPage> {
        return this.apiClientAssessmentService.getAssessment(instanceId).pipe(
            map(instance => this.mapInstanceOrigianlStateAndStore(instance)),
        );
    }

    /**
     * Fresh single GET for the share dialog. The list/Akita stores can be stale on section-visibility
     * toggles and invite-scope changes, so the dialog reads authoritative `sections` (for the
     * hidden-sections gate that reveals the Access control) and `collabInvites` straight from the
     * server rather than a possibly-stale store entry.
     */
    getAssessmentFromServer(instanceId: AssessmentResourceId): Observable<AssessmentPageResponse> {
        return this.apiClientAssessmentService.getAssessment(instanceId);
    }
    
    createInstance(request: AssessmentPageRequest): Observable<AssessmentPage> {
        return this.apiClientAssessmentService.createAssessment(request).pipe(
            map(instance => this.mapInstanceOrigianlStateAndStore(instance)),
        );
    }
        
    deleteInstance(id: AssessmentResourceId): Observable<void> {
        return this.apiClientAssessmentService.deleteAssessment(id).pipe(
            tap(() => this.removeInstanceFromStore(id))
        );
    }

    uploadInstanceAttachment(instanceId: AssessmentResourceId, file: File): Observable<AssessmentAttachment> {
        return this.apiClientAssessmentService.uploadInstanceAttachment(instanceId.toString(), file).pipe(
            catchError(error => {
                const message = error?.error?.includes('MaxAttachments') ? 'ai-assessments.attachmentMaxOperationsError' : 'ai-assessments.attachmentUploadError';
                this.snackbarService.showTimed(MineSnackbarType.Error, this.contentPipe.transform(message));
                this.loggerService.error(this.loggerName, `Upload attachment failed: ${error.error}`);
                console.error(error);
                throwError(() => error);
                return EMPTY;
            }),
        );
    }

    getInstanceAttachmentUrl(instanceId: AssessmentResourceId, attachmentId: string): Observable<string> {
        return this.apiClientAssessmentService.getInstanceAttachmentUrl(instanceId.toString(), attachmentId).pipe(
            catchError(error => {
                this.snackbarService.showTimed(MineSnackbarType.Error, this.contentPipe.transform('ai-assessments.attachmentDownloadError'));
                this.loggerService.error(this.loggerName, `Download attachment failed: ${error.error}`);
                console.error(error);
                return EMPTY;
            }),
        );
    }

    deleteInstanceAttachment(instanceId: AssessmentResourceId, attachmentId: string): Observable<AssessmentAttachment> {
        return this.apiClientAssessmentService.deleteInstanceAttachment(instanceId.toString(), attachmentId).pipe(
            catchError(error => {
                this.snackbarService.showTimed(MineSnackbarType.Error, this.contentPipe.transform('ai-assessments.attachmentDeleteError'));
                this.loggerService.error(this.loggerName, `Delete attachment failed: ${error.error}`);
                console.error(error);
                return EMPTY;
            }),
        );
    }

    private getDecodedInstance(instance: AssessmentPageResponse): AssessmentPageResponse {
        return { 
            ...instance, 
            name: this.htmlDecodePipe.transform(instance.name),
        } as AssessmentPageResponse;
    }

    /**
     * Merges the lightweight rows a bulk endpoint echoes back into the store, without a refetch.
     *
     * The rows carry the table's columns only, so they are spread OVER the stored page rather than
     * replacing it — `sections`, `layout`, `logic` and `originalState` are preserved. Deliberately
     * does NOT go through `mapInstanceOrigianlStateAndStore`: the mapper dereferences
     * `assessmentRes.sections` unguarded and would throw on a row, and it would also rebuild
     * `originalState`, wiping the baseline the change-tracking helper diffs against.
     *
     * Rows for assessments not currently in the store are skipped — nothing to merge onto.
     */
    upsertAssessmentRows(rows: AssessmentRow[]): void {
        (rows ?? []).forEach(row => this.upsertAssessmentRow(row));
    }

    private upsertAssessmentRow(row: AssessmentRow): void {
        const existing = this.getAssessmentById(row?.id);

        if (!existing) {
            return;
        }

        this.updateInstanceInStore({
            ...existing,
            ...row,
            sections: existing.sections,
            originalState: existing.originalState,
        });
    }

    mapInstanceOrigianlStateAndStore(instance: AssessmentPageResponse): AssessmentPage {
        const assessment = this.assessmentsMapperService.mapAssessmentPage(this.getDecodedInstance(instance));
        this.updateInstanceInStore(assessment);
        return assessment;
    }
        
    private updateInstanceInStore(assessment: AssessmentPage): void {
        const assessmentsStore = this.store.get('assessments') as Map<AssessmentUseCaseEnum, Map<AssessmentResourceId, AssessmentPage>>;
        if (!assessmentsStore) return;

        const useCase = this.assessmentsSplitByUseCaseService.selectUseCaseByLabelsOrType(assessment.type as AssessmentTemplateEnum, assessment.tags);

        const newAssessmentsStore = new Map(assessmentsStore);

        // The bucket key is derived from the assessment's use-case LABEL, so editing labels can move an
        // assessment between buckets. Evict it from wherever it lives
        // first: writing only to the new bucket would leave a stale copy — still carrying the removed
        // label — in the old one, and the unified table flattens every bucket, so that copy keeps
        // matching the removed label's tab until a full refetch rebuilds the store.
        const previousBucket = assessmentsStore.get(useCase);
        const existing = this.evictInstanceFromBuckets(newAssessmentsStore, assessment.id);

        this.evictSearchDoc(existing);
        this.evictSearchDoc(assessment);
        const assessmentToStore: AssessmentPage = {
            ...assessment,
            collabInvites: this.mergeCollabInvites(assessment, existing),
        };
        this.refreshSearchDoc(assessmentToStore);

        // Re-insert IN PLACE. The eviction above removed the id, and `Map.set` appends, so a plain set
        // would move every updated assessment to the end of its bucket — reordering the table on every
        // edit, and visibly scrambling it after a bulk action that touches many rows. Buckets are
        // iterated in insertion order and `sortAssessments` returns the list untouched when no column
        // sort is active, so that order IS the rendered order. Only a row that changed bucket (its
        // use-case labels were edited) is appended, where position carries no meaning anyway.
        const updatedMap = previousBucket?.has(assessment.id)
            ? this.replaceInBucket(previousBucket, assessment.id, assessmentToStore)
            : new Map(newAssessmentsStore.get(useCase) ?? new Map<AssessmentResourceId, AssessmentPage>())
                .set(assessment.id, assessmentToStore);

        newAssessmentsStore.set(useCase, updatedMap);

        this.store.set('assessments', () => newAssessmentsStore);
    }

    /** Copies a bucket with one entry swapped for an updated record, keeping every position unchanged. */
    private replaceInBucket(
        bucket: Map<AssessmentResourceId, AssessmentPage>,
        id: AssessmentResourceId,
        assessment: AssessmentPage,
    ): Map<AssessmentResourceId, AssessmentPage> {
        const rebuilt = new Map<AssessmentResourceId, AssessmentPage>();
        bucket.forEach((value, key) => rebuilt.set(key, key === id ? assessment : value));
        return rebuilt;
    }

    /**
     * Removes an assessment id from every use-case bucket of the given (already copied) top-level map,
     * replacing each touched bucket immutably, and returns the record it held — the caller re-inserts it
     * under the bucket its current labels resolve to. Also self-heals duplicates left by earlier writes.
     */
    private evictInstanceFromBuckets(
        assessmentsStore: Map<AssessmentUseCaseEnum, Map<AssessmentResourceId, AssessmentPage>>,
        id: AssessmentResourceId,
    ): AssessmentPage | undefined {
        let existing: AssessmentPage | undefined;

        assessmentsStore.forEach((assessments, useCase) => {
            const found = assessments.get(id);
            if (!found) {
                return;
            }
            existing = found;
            const withoutInstance = new Map(assessments);
            withoutInstance.delete(id);
            assessmentsStore.set(useCase, withoutInstance);
        });

        return existing;
    }

    private mergeCollabInvites(incoming: AssessmentPage, existing: AssessmentPage | undefined): AssessmentInvitee[] | undefined {
        // The approval/state-update API omits collabInvites rather than returning []
        // when collaborators haven't changed. Preserve existing to avoid clearing the
        // share dialog. If the API ever sends [] to explicitly clear collaborators,
        // this logic will need revisiting.
        if (incoming.collabInvites?.length) {
            return incoming.collabInvites;
        }
        return existing?.collabInvites ?? incoming.collabInvites;
    }

    /**
     * Removes an assessment instance from its corresponding internal map
     * (grouped by use case) by assessment id.
     * @param id AssessmentResourceId
     */
    private removeInstanceFromStore(id: AssessmentResourceId): void {
        const assessmentsStore = this.store.get('assessments') as Map<AssessmentUseCaseEnum, Map<AssessmentResourceId, AssessmentPage>>;

        // Find the entry (use case map) containing this assessment id
        for (const [useCase, assessmentsMap] of assessmentsStore.entries()) {
            if (assessmentsMap.has(id)) {
                // Create a new map with the assessment removed for immutability
                const updatedMap = new Map(assessmentsMap.entries());
                updatedMap.delete(id);

                // Build new top-level store map with this updated map
                const newAssessmentsStore = new Map(assessmentsStore.entries());
                newAssessmentsStore.set(useCase, updatedMap);

                this.store.set('assessments', () => newAssessmentsStore);
                break;
            }
        }
    }

    buildSchemaBySections(sections: AssessmentSection[] | AssessmentsMineOSFormControl[]): string {
        const schemaArr = [];
        
        if (!sections?.length) return JSON.stringify(schemaArr);
        sections.forEach(section => {
            schemaArr.push({
                ...section,
                controls: section.controls,
                type: section.type === AssessmentQuestionTypeEnum.NewSection ? AssessmentQuestionTypeEnum.Group : section.type
            });
        });

        return JSON.stringify(this.assessmentsLogicManagerService.generateSectionsUuid(schemaArr));
    }

    createTemplate(request: AssessmentsTemplateRequest): Observable<AssessmentsTemplate> {
        return this.apiClientAssessmentService.createTemplate(request).pipe(
            tap(template => this.updateTemplateInStore(template))
        );
    }

    updateTemplate(request: AssessmentsTemplate): Observable<void> {
        return this.apiClientAssessmentService.updateTemplate(request).pipe(
            tap(() => this.updateTemplateInStore(request)),
        );
    }

    deleteTemplate(templateId: string): Observable<void> {
        return this.apiClientAssessmentService.deleteTemplate(templateId).pipe(
            tap(() => this.removeTemplateFromStore(templateId))
        );
    }

    cloneTemplate(template: AssessmentsTemplate): Observable<AssessmentsTemplate> {
        const { id, ...clonedTemplate } = template;
        
        if (!clonedTemplate.tagIds && clonedTemplate.tags?.length) {
            clonedTemplate.tagIds = clonedTemplate.tags.map(tag => tag.id as number);
        }
        
        return this.apiClientAssessmentService.createTemplate(clonedTemplate as AssessmentsTemplateRequest).pipe(
            tap(createdTemplate => this.updateTemplateInStore(createdTemplate))
        );
    }

    private updateTemplateInStore(template: AssessmentsTemplate): void {
        const useCase = this.assessmentsSplitByUseCaseService.selectUseCaseByLabelsOrType(template.type as AssessmentTemplateEnum, template.tags);
        
        this.updateTemplatesStore(useCase, (useCaseMap) => {
            const newUseCaseMap = new Map<AssessmentResourceId, AssessmentsTemplate>(useCaseMap);
            newUseCaseMap.set(template.id, template);
            return newUseCaseMap;
        }, () => {
            // If use case doesn't exist, create it with the new template
            return new Map([[template.id as AssessmentResourceId, template]]);
        });
    }

    private removeTemplateFromStore(id: string): void {
        const template = this.getTemplateById(id as AssessmentResourceId);
        const useCase = this.assessmentsSplitByUseCaseService.selectUseCaseByLabelsOrType(template.type as AssessmentTemplateEnum, template.tags);
        
        this.updateTemplatesStore(useCase, (useCaseMap) => {
            // Create a new inner Map without the deleted template
            const newUseCaseMap = new Map<AssessmentResourceId, AssessmentsTemplate>();
            useCaseMap.forEach((templateItem, templateId) => {
                if (templateId !== id) {
                    newUseCaseMap.set(templateId, templateItem);
                }
            });
            return newUseCaseMap;
        });
    }

    private updateTemplatesStore(
        useCase: AssessmentUseCaseEnum,
        updateUseCaseMap: (useCaseMap: Map<AssessmentResourceId, AssessmentsTemplate>) => Map<AssessmentResourceId, AssessmentsTemplate>,
        createUseCaseMap?: () => Map<AssessmentResourceId, AssessmentsTemplate>
    ): void {
        const templatesStore = this.store.get('templates') as Map<AssessmentUseCaseEnum, Map<AssessmentResourceId, AssessmentsTemplate>>;
        if (!templatesStore) return;

        // Create a new Map structure to trigger signal change detection
        const newTemplatesStore = new Map<AssessmentUseCaseEnum, Map<AssessmentResourceId, AssessmentsTemplate>>();
        const useCaseExists = templatesStore.has(useCase);
        
        templatesStore.forEach((useCaseMap, useCaseKey) => {
            if (useCaseKey === useCase) {
                // Update the use case map with the provided update function
                const updatedMap = updateUseCaseMap(useCaseMap);
                newTemplatesStore.set(useCaseKey, updatedMap);
            } else {
                // Keep the existing inner Map for other use cases
                newTemplatesStore.set(useCaseKey, useCaseMap);
            }
        });

        // If use case doesn't exist and create function is provided, create it
        if (!useCaseExists && createUseCaseMap) {
            newTemplatesStore.set(useCase, createUseCaseMap());
        }
        
        this.store.set({ templates: newTemplatesStore });
    }

    getUniqueControlNameSet(controls: Array<AssessmentSection> | Array<AssessmentQuestion> | Array<AssessmentsMineOSFormControl>): Set<string> {
        const set = new Set<string>();
        for (let control of controls) {
          // Skip deleted questions when building unique name set for validation
          if ('isDeleted' in control && control.isDeleted) {
            continue;
          }
          set.add(control.label?.toLowerCase());
        }
        return set;
    }

    generateNewSection(name?: string, template?: boolean): Pick<AssessmentSectionRequest, 'name' | 'label'> & { formControlId: string } | AssessmentsMineOSFormControl {
        const defaultSectionLabel = this.contentPipe.transform('ai-assessments.defaultSectionLabel');

        if (template) {
            const section = {
                type: AssessmentQuestionTypeEnum.NewSection,
                name: !!name ? this.camelCaseWithSpacesPipe.transform(name) : defaultSectionLabel,
                label: name ?? defaultSectionLabel,
                controls: []
            } as AssessmentsMineOSFormControl;

            section.id = this.assessmentsLogicManagerService.generateSectionUuid(section);
            return section;
        }

        return {
            isNew: true,
            formControlId: this.randomUUIDPipe.transform(),
            name: !!name ? this.camelCaseWithSpacesPipe.transform(name) : defaultSectionLabel,
            label: name ?? defaultSectionLabel,
            questions: [],
        } as Pick<AssessmentSectionRequest, 'name' | 'label'> & { formControlId: string };
    }

    generateNewTemplateSection(name?: string): AssessmentsMineOSFormControl {
        const defaultSectionLabel = this.contentPipe.transform('ai-assessments.defaultSectionLabel');

        const section = {
                
        } as AssessmentsMineOSFormControl;

        return section;
    }

    validateControlLabel(control: AssessmentQuestion | AssessmentsMineOSFormControl): boolean {
        return !control.label?.trim();
    }

    isNewSectionIsEmpty(section: AssessmentsMineOSFormControl): boolean {
        if (section.type === AssessmentQuestionTypeEnum.NewSection) {
            return !section.controls?.length;
        }
        return false;
    }

    isGroupSectionIsEmpty(section: AssessmentSection | AssessmentsMineOSFormControl): boolean {
        const questions = this.questionTypeHelper.getSectionQuestions(section);
        return !questions?.length;
    }

    hasDropdownsWithoutOptions(sections: AssessmentSection[] | AssessmentsMineOSFormControl[]): boolean {
        const condition: StatefulConditionFunction = (section: AssessmentSection | AssessmentsMineOSFormControl, state) => {
            const questions = this.questionTypeHelper.getSectionQuestions(section);
            return [questions.some(question => question.type === AssessmentQuestionTypeEnum.Dropdown && !(question.value as AssessmentDropdownOption[])?.length), state];
        };
        return this.validateSections(sections, condition, null);
    }

    // Template-builder validation: a Dropdown/Multiple-choice question keeps its editable options in
    // `control.options` (not the instance-time `question.value`), so block save when it has no options
    // or any option label is blank — mirroring the per-option "required" error in the builder template.
    hasTemplateDropdownsWithEmptyOptions(sections: AssessmentsMineOSFormControl[]): boolean {
        const dropdownTypes = [AssessmentQuestionTypeEnum.Dropdown, AssessmentQuestionTypeEnum.DropdownCustomField];
        const condition: StatefulConditionFunction = (control: AssessmentsMineOSFormControl, state) => {
            const type = this.assessmentsMapperService.getQuestionType({ type: control.type, name: control.name, customFieldId: control.customFieldId });
            if (!dropdownTypes.includes(type)) {
                return [false, state];
            }
            const options = control.options ?? [];
            // `?.` stays: option labels are `null` at runtime (e.g. onAddOption) despite the `string` type.
            const invalid = !options.length || options.some(option => !option.value?.trim().length);
            return [invalid, state];
        };
        return this.validateSections(sections, condition, null);
    }

    private validateSections(sections: AssessmentSection[] | AssessmentsMineOSFormControl[], condition: StatefulConditionFunction, initialState: any): boolean {
        let state = initialState;
        for (let section of sections) {
            const questions = this.questionTypeHelper.getSectionQuestions(section);
            if (questions?.length) {
                for (let control of questions) {
                    const [result, newState] = condition(control, state);
                    if (result) {
                        this.scrollToFirstErrorOnDom();
                        return true;
                    }
                    state = newState;
                }
            }
        }
        return false;
    }

    /**
     * Smooth-scrolls a rendered question into view. Both the agent and the collaborator form render questions as
     * `.form__group--<sectionFormControlId> .form__control--<questionFormControlId>`.
     */
    scrollToQuestionOnDom(sectionFormControlId: AssessmentResourceId, questionFormControlId: AssessmentResourceId): void {
        // 150 ms: callers scroll right after a form change (e.g. a question moved to another section); the re-render
        // has to land first or the selector misses / measures the old position.
        setTimeout(() => {
            const questionElem = document.querySelector(`.form__group--${sectionFormControlId} .form__control--${questionFormControlId}`);
            questionElem?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        }, 150);
    }

    scrollToFirstErrorOnDom(): void {
        setTimeout(() => {
            const errorElement = document.querySelector('[class*="--error"]');
            if (!!errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return;
            }
    
            window.scrollTo({
                top: document.documentElement.scrollHeight,
                behavior: 'smooth'
            });
        }, 0);
    }
    
    replaceTemplateType(assessment: AssessmentPage): AssessmentPage {
        const { type } = assessment;
        const cmsTypes = this.contentPipe.transform('ai-assessments.types');
        return { ...assessment, type: cmsTypes[AssessmentTemplateEnum[type]] ?? cmsTypes[AssessmentTemplateEnum.Empty] };
    }

    async buildInstancesForExport(assessments: AssessmentPage[], ignoreIncompleteFields: boolean): Promise<AssessmentExportTemplateInstance[]> {
        const ownerIds = assessments.map(({ ownerId }) => ownerId).filter(Boolean);
        const employees = await this.aiAssessmentsExportHelper.getBusinessOwnerData(assessments, ownerIds);

        return Promise.all(assessments.map(async instance =>
            this.aiAssessmentsExportHelper.buildInstanceForExport(
                this.replaceTemplateType(instance),
                employees,
                ignoreIncompleteFields,
            )
        ));
    }

    private collectInstanceImages(instances: AssessmentExportTemplateInstance[]): Observable<AssessmentExportTemplateInstance[]> {
        const instancesWithoutImages = [...instances];
        const imagesByUrl$ = from(this.aiAssessmentsExportHelper.extractImagesInBase64(instances));
        const companyIconObject$ = this.profileQuery.selectCompanyIcon().pipe(
            switchMap((companyIcon) => combineLatest([this.aiAssessmentsExportHelper.imageToBase64(companyIcon), of(companyIcon)])),
            map(([base64, companyIcon]) => {
                return { [companyIcon]: base64 };
            }),
        );
        return combineLatest([companyIconObject$, imagesByUrl$]).pipe(
            map(([companyIconObject, otherImagesObject]) => {
                const imagesByUrl = { ...companyIconObject, ...otherImagesObject };
                instancesWithoutImages[0].images = imagesByUrl;
                this.loggerService.info(this.loggerName, `${Object.keys(imagesByUrl).length} images converted to base64.`);
                return instancesWithoutImages;
            }),
        );
    }

    exportInstances(title: string, assessments: AssessmentPage[], metaData: Partial<AssessmentExportMetadata>, format: 'pdf' | 'docx' = 'pdf'): Observable<ArrayBuffer> {
        this.loggerService.info(this.loggerName, `Starting ${format.toUpperCase()} export: exporting ${assessments?.length ?? '0'} instances...`);
        const instances$ = from(this.buildInstancesForExport(assessments, metaData.ignoreIncompleteFields));
        const ropaDetails$ = this.companySettingsQuery.selectRopaDetails();
        const fullName$ = this.profileQuery.selectFullname();
        const instancesWithImages$ = instances$.pipe(
            switchMap(instances => this.collectInstanceImages(instances)),
        );

        this.dialogRef = this.dialogService.openDialogCommon(MineAsyncOperationSpinnerDialogComponent, this.contentPipe.transform('pdf-generator.generatingPdfReportTitle'), '442px', false);

        return combineLatest([instancesWithImages$, ropaDetails$, fullName$]).pipe(
            first(),
            switchMap(([instances, ropaDetails, agentName]) => this.pdfGeneratorService.sendRequest({
                title,
                exportType: ExportTypeEnum.Assessment,
                format,
                squidexContents: [ContentSchemaHelper.PdfGenerator],
                data: {
                    instances,
                    metaData: {
                        ...metaData,
                        ropaDetails,
                        agentName,
                        riskScale1to5: true
                    },
                }
            })),
            catchError(error => {
                this.snackbarService.showTimed(MineSnackbarType.Error, this.contentPipe.transform('pdf-generator.exportError'));
                this.loggerService.error(this.loggerName, `PDF export failed: ${error.message}`);
                console.error(error);
                return EMPTY;
            }),
            finalize(() => this.dialogRef?.close()),
        );
    }

    getOptionsForNewDropdownControl(inputOptions?: CustomFieldOption[]): DropdownOption[] {
        const defaultOption = [{ id: `o-${this.randomUUIDPipe.transform()}`, value: null } as DropdownOption];
        return (inputOptions?.length > 0 ? inputOptions.map(o => ({ id: o?.id, value: o?.name, chipColor: {backgroundColor: o?.color, textColor: '--mine-blue-dark'}} as DropdownOption)) : defaultOption);
    }

    private setFunctions(res: string[]): void {
		this.functions.next(res?.sort((a, b) => {
			// make sure cross functional function will be the latest
			if (a?.toLowerCase() === this.CROSS_FUNCTIONAL_FUNCTION_NAME.toLowerCase()) {
				return 1;
			}
			else if (b?.toLowerCase() === this.CROSS_FUNCTIONAL_FUNCTION_NAME.toLowerCase()) {
				return -1;
			}
			return a?.toLowerCase().localeCompare(b?.toLowerCase());
		}));
	}

    getFunctions(): string[] {
		return this.functions.value;
	}

    getLockStatus(instanceId: AssessmentResourceId): Observable<AssessmentLockStatus> {
        return this.apiClientAssessmentService.getLockStatus(instanceId.toString());
    }

    setLockStatus(instanceId: AssessmentResourceId, isLocked: boolean): Observable<AssessmentLockStatus> {
        return this.apiClientAssessmentService.setLockStatus(instanceId.toString(), isLocked)
            .pipe(
                tap(res => this.aiAssessmentsLockStatusService.updateLockStatus(res))
            );
    }

    customControlNameToEntityType(controlName: AssessmentQuestionTypeEnum): string {
        switch (controlName) {
            case AssessmentQuestionTypeEnum.BusinessOwner:
                return 'BusinessOwner';
            
            case AssessmentQuestionTypeEnum.BusinessPurpose:
                return 'BusinessPurpose';
            
            case AssessmentQuestionTypeEnum.BusinessUnit:
                return 'BusinessUnit';
            
            case AssessmentQuestionTypeEnum.DataRole:
                return 'DataRole';
            
            case AssessmentQuestionTypeEnum.DataSources:
                return 'System';
            
            case AssessmentQuestionTypeEnum.DataSubjects:
                return 'DataSubject';
            
            case AssessmentQuestionTypeEnum.DataTypes:
                return 'DataType';
            
            case AssessmentQuestionTypeEnum.LegalBasis:
                return 'LegalBasis';

            case AssessmentQuestionTypeEnum.LinkedAssessment:
                return 'LinkedAssessment';

            case AssessmentQuestionTypeEnum.PartiesWithAccess:
                return 'PartiesWithAccess';

            case AssessmentQuestionTypeEnum.TransferMechanism:
                return 'CrossBorder';
        }
    }

    setFullFiltersList(filters: FilterCategory[]): void {
        this.fullFiltersList = filters;
    }

    /**
     * Project a saved view's selection onto the current filter list for the rail counters, using the
     * SAME merge the table uses (`AssessmentsTableService.updateFilters`). Previously this had its own
     * copy that DROPPED any stored option missing from `fullFiltersList`, so a legacy id like the
     * Collaborators `Submitted` left the category with nothing selected — `filterAssessments` then
     * skipped it and the counter reported the unfiltered total while the table rendered none.
     *
     * `fullFiltersList` is populated by the table store, so it can still be unset when the rail builds
     * its counts on a cold load; the raw saved filters are the correct fallback and now agree with the
     * merged result either way.
     */
    private updateFilters(newFilters: FilterCategory[]): FilterCategory[] {
        return this.fullFiltersList ? mergeFilterSelections(this.fullFiltersList, newFilters) : newFilters;
    }

    getRelatedEntitiesByAssessment(assessment: AssessmentPage, type: AssessmentQuestionTypeEnum, customFieldId?: string): AssessmentResourceValue {
        // A standalone risk (DevRiskRegistryAddRisk) has no assessment; the registry's Data sources column and CSV export
        // pass it through here and expect "none" rather than a throw.
        if (!assessment) return [];

        if (type === AssessmentQuestionTypeEnum.DataSources && assessment.vendorSpecificId) {
            return [assessment.vendorSpecificId];
        }

        const question = this.findAssessmentQuestionByType(assessment, type, customFieldId);
        if (!question) return [];

        const value = question.value as AssessmentResourceValue;
        if (!value) return [];

        return value;
	}

	private findAssessmentQuestionByType(assessment: AssessmentPage, type: AssessmentQuestionTypeEnum, customFieldId?: string): AssessmentQuestion {
        const questions = assessment.sections?.flatMap(section => section.questions);
        if (customFieldId) {
            return questions.filter(q => (q.value as AssessmentCustomFieldDropdown | AssessmentCustomFieldText)?.customFieldId === customFieldId)?.find(question => question.type === type);
        }
        return questions?.find(question => question.type === type);
	}

    executeAssessmentChanges(assessment: AssessmentPage): Observable<SubmissionState> {
        const serverAssessment = this.isCollaborator ?
            this.selectInstance(assessment.id) :
            of(this.getAssessmentById(assessment.id));

        return serverAssessment.pipe(
            switchMap(serverAssessment => {
                const changes = this.assessmentsTrackingChangesHelper.getChangesPayload(assessment);
                const validatedOperations = this.validateOperationsAgainstServerState(changes, serverAssessment);

                if (!validatedOperations.length) {
                    return this.executeOrdinalityChanges(assessment);
                }

                const { sequential, parallel } = this.partitionOperations(validatedOperations);
                const skipIntermediateSync = parallel.length > 1;

                const sequentialPhase$ = sequential.length
                    ? this.executeOperationsSequentially(assessment, sequential, skipIntermediateSync)
                    : of({ completed: [], remaining: [] } as SubmissionState);

                return sequentialPhase$.pipe(
                    switchMap(seqState => {
                        if (seqState.failed) {
                            return of({
                                ...seqState,
                                remaining: [...seqState.remaining, ...parallel],
                            } as SubmissionState);
                        }

                        if (!parallel.length) {
                            return of(seqState);
                        }

                        return this.executeOperationsInParallel(assessment, parallel, skipIntermediateSync).pipe(
                            map(parallelResults => {
                                const firstFailure = parallelResults.find(r => !r.success);
                                const completed = [...seqState.completed, ...parallelResults.filter(r => r.success)];
                                return firstFailure
                                    ? { completed, failed: firstFailure, remaining: [] } as SubmissionState
                                    : { completed, remaining: [] } as SubmissionState;
                            })
                        );
                    }),
                    switchMap(state => {
                        if (state.failed) {
                            return of(state);
                        }
                        const updatedAssessment = this.updateAssessmentWithCreatedIds(assessment, state);
                        return this.executeOrdinalityChanges(updatedAssessment);
                    }),
                    finalize(() => {
                        if (skipIntermediateSync) {
                            this.mapInstanceOrigianlStateAndStore(this.getAssessmentById(assessment.id));
                        }
                    }),
                );
            })
        );
    }

    private executeOrdinalityChanges(assessment: AssessmentPage): Observable<SubmissionState> {
        const changes = this.assessmentsTrackingChangesHelper.getOrdinalityChangesPayload(assessment);
        // Cross-section move is split by concern: the move endpoint re-parents the question (FK)
        // and re-keys conditional logic, while the CLIENT stays the single source of truth for
        // section/question ORDER. The client is the only place that knows the exact order the user
        // sees at save time across a batched edit (move + reorder + add + delete), so we always
        // PATCH every changed section's layout with that order — including the move's source and
        // target. We must NOT defer their ordinality to the BE: the endpoint only knows
        // {questionId, source, target}, cannot honour arbitrary reorders, and its append writes a
        // format the client can't read (a bare id mixed into the tuple array), which corrupts the
        // rendered order. Ordering guarantee: move ops run before the ordinality PATCHes below, so
        // the question is already re-parented when its section layout is set.
        const moves = this.assessmentsTrackingChangesHelper.getQuestionMoves(assessment);
        const moveOperations = this.buildQuestionMoveOperationsQueue(moves);
        const ordinalityOperations = this.buildOrdinalityOperationsQueue(changes);
        const operations = [...moveOperations, ...ordinalityOperations];

        if (!operations.length) {
            return of({
                completed: [],
                remaining: []
            } as SubmissionState);
        }

        operations.push({
            id: this.randomUUIDPipe.transform(),
            type: 'assessment_reload',
            data: assessment.id as AssessmentResourceId,
        });

        return this.executeOperationsSequentially(assessment, operations);
    }

    private validateOperationsAgainstServerState(changes: ChangesPayload, serverAssessment: AssessmentPage): AssessmentOperation[] {
        // Create sets of existing server section and question IDs for fast lookup
        const serverSectionIds = new Set<AssessmentResourceId>();
        const serverQuestionMap = new Map<AssessmentResourceId, Set<AssessmentResourceId>>(); // sectionId -> questionIds
        
        serverAssessment.sections?.forEach(section => {
            if (section.id) {
                serverSectionIds.add(section.id);
                const questionIds = new Set<AssessmentResourceId>();
                section.questions?.forEach(question => {
                    if (question.id) {
                        questionIds.add(question.id);
                    }
                });
                serverQuestionMap.set(section.id, questionIds);
            }
        });
        
        // Compute valid deleted IDs upfront so we can filter redundant updates in the same batch
        const validDeletedSectionIds = new Set<AssessmentResourceId>(
            changes.section.deleted.filter(id => serverSectionIds.has(id))
        );

        const validDeletedQuestionIds = new Set<AssessmentResourceId>();
        const validDeletedQuestionKeys = changes.question.deleted.filter(questionKey => {
            const questionKeyStr = String(questionKey);
            const [sectionIdStr, questionIdStr] = questionKeyStr.split('-');
            const sectionId = isNaN(Number(sectionIdStr)) ? sectionIdStr : Number(sectionIdStr) as AssessmentResourceId;
            const questionId = isNaN(Number(questionIdStr)) ? questionIdStr : Number(questionIdStr) as AssessmentResourceId;
            const sectionQuestions = serverQuestionMap.get(sectionId);
            const exists = sectionQuestions?.has(questionId) || false;
            if (exists) {
                validDeletedQuestionIds.add(questionId);
            }
            return exists;
        });

        const validChanges: ChangesPayload = {
            assessment: changes.assessment,
            section: {
                created: changes.section.created,
                // Skip updates for sections being deleted in the same batch
                updated: changes.section.updated.filter(update =>
                    !update.sectionId || !validDeletedSectionIds.has(update.sectionId)
                ),
                deleted: [...validDeletedSectionIds],
            },
            question: {
                created: changes.question.created,
                // Skip updates for questions (or whole sections) being deleted in the same batch
                updated: changes.question.updated.filter(update =>
                    (!update.sectionId || !validDeletedSectionIds.has(update.sectionId)) &&
                    (!update.questionId || !validDeletedQuestionIds.has(update.questionId))
                ),
                deleted: validDeletedQuestionKeys,
            }
        };
        
        return this.buildOperationsQueue(validChanges);
    }

    private buildOperationsQueue(changes: ChangesPayload): AssessmentOperation[] {
        const operations: AssessmentOperation[] = [];

        // delete operations first
        if (changes.section.deleted.length) {
            changes.section.deleted.forEach((sectionId, index) => {
                operations.push({
                    id: this.randomUUIDPipe.transform(),
                    type: 'section_delete',
                    data: sectionId,
                });
            });
        }

        if (changes.question.deleted.length) {
            changes.question.deleted.forEach((questionId, index) => {
                operations.push({
                    id: this.randomUUIDPipe.transform(),
                    type: 'question_delete',
                    data: questionId,
                });
            });
        }

        // create operations
        if (changes.section.created.length) {
            changes.section.created.forEach((section, index) => {
                operations.push({
                    id: this.randomUUIDPipe.transform(),
                    type: 'section_create',
                    data: section as AssessmentSectionRequest,
                });
            });
        }

        if (changes.question.created.length) {
            changes.question.created.forEach((question, index) => {
                operations.push({
                    id: this.randomUUIDPipe.transform(),
                    type: 'question_create',
                    data: question as AssessmentQuestionRequest,
                });
            });
        }

        // update operations
        if (changes.question.updated.length) {
            changes.question.updated.forEach((question, index) => {
                operations.push({
                    id: this.randomUUIDPipe.transform(),
                    type: 'question_update',
                    data: question,
                });
            });
        }

        if (changes.section.updated.length) {
            changes.section.updated.forEach((section, index) => {
                operations.push({
                    id: this.randomUUIDPipe.transform(),
                    type: 'section_update',
                    data: section,
                });
            });
        }

        if (changes.assessment.updated.length) {
            changes.assessment.updated.forEach((assessment, index) => {
                operations.push({
                    id: this.randomUUIDPipe.transform(),
                    type: 'assessment_update',
                    data: assessment,
                });
            });
        }

        return operations;
    }

    private buildQuestionMoveOperationsQueue(moves: AssessmentQuestionMoveRequest[]): AssessmentOperation[] {
        return moves.map(move => ({
            id: this.randomUUIDPipe.transform(),
            type: 'question_move' as AssessmentOperationType,
            data: move,
        }));
    }

    private buildOrdinalityOperationsQueue(changes: OrdinalityChangesPayload): AssessmentOperation[] {
        const operations: AssessmentOperation[] = [];
        if (changes.section.updated.length) {
            changes.section.updated.forEach((section, index) => {
                operations.push({
                    id: this.randomUUIDPipe.transform(),
                    type: 'section_update',
                    data: section,
                });
            });
        }
        if (changes.assessment.updated.length) {
            changes.assessment.updated.forEach((assessment, index) => {
                operations.push({
                    id: this.randomUUIDPipe.transform(),
                    type: 'assessment_update',
                    data: assessment,
                });
            });
        }
        return operations;
    }

    private partitionOperations(
        operations: AssessmentOperation[]
    ): { sequential: AssessmentOperation[]; parallel: AssessmentOperation[] } {
        const sequential: AssessmentOperation[] = [];
        const parallel: AssessmentOperation[] = [];

        for (const operation of operations) {
            if (this.PARALLELIZABLE_OPERATIONS.has(operation.type)) {
                parallel.push(operation);
            } else {
                sequential.push(operation);
            }
        }

        return { sequential, parallel };
    }

    private executeOperationsInParallel(
        assessment: AssessmentPage,
        operations: AssessmentOperation[],
        skipSync = false
    ): Observable<SubmissionOperationResult[]> {
        if (!operations.length) {
            return of([]);
        }

        return from(operations).pipe(
            mergeMap(
                (operation) => this.executeOperation(assessment, operation, skipSync).pipe(
                    map(result => ({
                        operationType: operation.type,
                        operationData: operation.data,
                        operationId: operation.id,
                        success: true,
                        result,
                    } as SubmissionOperationResult)),
                    catchError(error => of({
                        operationType: operation.type,
                        operationData: operation.data,
                        operationId: operation.id,
                        success: false,
                        error,
                    } as SubmissionOperationResult))
                ),
                this.MAX_CONCURRENT_OPERATIONS
            ),
            toArray()
        );
    }

    private executeOperationsSequentially(
        assessment: AssessmentPage,
        operations: AssessmentOperation[],
        skipSync = false
    ): Observable<SubmissionState> {
        const initialState: SubmissionState = {
            completed: [],
            remaining: [...operations]
        };

        return from(operations).pipe(
            concatMap((operation, index) => {
                return this.executeOperation(assessment, operation, skipSync).pipe(
                    map(result => ({
                        operation,
                        result,
                        index,
                        success: true
                    })),
                    catchError(error => {
                        return of({
                            operation,
                            result: null,
                            index,
                            success: false,
                            error
                        });
                    })
                );
            }),
            scan((state: SubmissionState, current: any) => {
                const operationResult: SubmissionOperationResult = {
                    operationType: current.operation.type,
                    operationData: current.operation.data,
                    operationId: current.operation.id,
                    success: current.success,
                    result: current.result,
                    error: current.error
                };

                // Remove the current operation from remaining
                const newRemaining = state.remaining.filter((_, i) => i !== current.index);

                if (current.success) {
                    return {
                        completed: [...state.completed, operationResult],
                        remaining: newRemaining
                    };
                } 

                return {
                    completed: [...state.completed],
                    failed: operationResult,
                    remaining: newRemaining
                };
            }, initialState),
            // Stop execution if we encounter a failure
            takeWhile((state: SubmissionState) => !state.failed, true),
            // Return the final state
            last()
        );
    }

    /**
     * Expands a tracked question-update op into the JSON-Patch body the BE expects.
     * Persistent path: a `/value` op plus, when the `reasoning` carrier
     * is present, a separate `/reasoning` op (`value: null` clears the evidence).
     * Pre-persistence path (fallback): a single op with reasoning kept as a
     * sibling field.
     * Routing fields (sectionId/questionId) are always stripped from the wire ops.
     */
    /**
     * Whether the split `/value` + `/reasoning` PATCH (evidence persistence) is on.
     * Always on for agents and the collaborator surface — mirrors the
     * tracking-changes helper so the diff format and the wire format stay in sync.
     */
    private persistentReasoningEnabled(): boolean {
        return true;
    }

    private buildQuestionPatchBody(data: AssessmentEditRequest): JsonPatchRequest[] {
        const { sectionId, questionId, reasoning, ...op } = data;
        // Must use the same predicate as the diff builder (persistentReasoningEnabled)
        // so the wire format matches; otherwise a deleted evidence's `/reasoning: null`
        // op would never be emitted and stale evidence would persist for other agents.
        if (!this.persistentReasoningEnabled()) {
            const legacyOp: AssessmentEditRequest = reasoning !== undefined ? { ...op, reasoning } : op;
            return [legacyOp];
        }
        const ops: JsonPatchRequest[] = [op];
        if (reasoning !== undefined) {
            ops.push({ op: AssessmentEditOpEnum.Replace, path: '/reasoning', value: reasoning ?? null });
        }
        return ops;
    }

    private executeOperation(
        assessment: AssessmentPage,
        operation: AssessmentOperation,
        skipSync = false
    ): Observable<AssessmentPage | AssessmentSection | AssessmentQuestion | void> {
        const { sectionId, questionId, ...payload } = operation.data as AssessmentEditRequest;

        switch (operation.type) {
            case 'assessment_update':
                return this.updateAssessment(assessment.id, [payload], skipSync);

            case 'section_create':
                return this.createSection(assessment.id, operation.data as AssessmentSectionRequest, skipSync);

            case 'section_update':
                return this.updateSection(assessment.id, sectionId, [payload], skipSync);

            case 'section_delete':
                return this.deleteSection(assessment.id, operation.data as AssessmentResourceId);

            case 'question_create':
                return this.createQuestion(assessment.id, (operation.data as AssessmentQuestionRequest), skipSync);

            case 'question_update':
                return this.updateQuestion(assessment.id, sectionId, questionId, this.buildQuestionPatchBody(operation.data as AssessmentEditRequest), skipSync);

            case 'question_delete':
                const ids = (operation.data as string).split('-');
                return this.deleteQuestion(assessment.id, ids[0], ids[1]);

            case 'question_move':
                return this.moveQuestionToSection(assessment.id, operation.data as AssessmentQuestionMoveRequest);

            case 'assessment_reload':
                return this.selectInstance(assessment.id);

            default:
                return throwError(() => new Error(`Unknown operation type: ${operation.type}`));
        }
    }
    
    extractQuestionDetails(assessment: AssessmentPage, type: AssessmentQuestionTypeEnum): {
      sectionId: AssessmentResourceId;
      questionId: AssessmentResourceId;
      value: any;
    } {
      for (const section of assessment.sections) {
        for (const question of section.questions) {
          if (question.type === type) {
            return {
              sectionId: section.id,
              questionId: question.id,
              value: question.value
            };
          }
        }
      }
      return null;
    }

    private updateAssessmentWithCreatedIds(assessment: AssessmentPage, state: SubmissionState): AssessmentPage {
        // Create a deep copy of the assessment to avoid mutating the original
        const updatedAssessment = structuredClone(assessment);
        
        // Map section creation results back to local sections
        state.completed?.forEach(operation => {
            if (operation.operationType === 'section_create') {
                const sectionRequest = operation.operationData as AssessmentSectionRequest;
                const createdSection = operation.result as AssessmentSection;
                
                if (sectionRequest && createdSection) {
                    // Find the local section that matches this creation request
                    const localSectionIndex = updatedAssessment.sections.findIndex(s => 
                        s.formControlId === sectionRequest.formControlId
                    );
                    
                    if (localSectionIndex !== -1) {
                        // Update the local section with the server-assigned ID
                        updatedAssessment.sections[localSectionIndex] = { ...createdSection };
                    }
                }
            }

            if (operation.operationType === 'question_create') {
                const questionRequest = operation.operationData as AssessmentQuestionRequest;
                const createdQuestion = operation.result as AssessmentQuestion;
                
                if (questionRequest && createdQuestion) {
                    // Find the section that this question belongs to
                    const targetSectionIndex = updatedAssessment.sections.findIndex(s => 
                        (s.id === questionRequest.assessmentSectionId)
                    );
                    
                    if (targetSectionIndex !== -1) {
                        // Find the local question that matches this creation request
                        const localQuestionIndex = updatedAssessment.sections[targetSectionIndex].questions?.findIndex(q => 
                            q.formControlId === questionRequest.formControlId
                        );
                        
                        if (localQuestionIndex !== -1) {
                            // Update the local question with the server-assigned ID
                            updatedAssessment.sections[targetSectionIndex].questions[localQuestionIndex] = { ...createdQuestion };
                        }
                    }
                }
            }
        });
        
        return updatedAssessment;
    }

    submitInstance(request: AssessmentPage): Observable<AssessmentPageResponse> {
        return this.apiClientAssessmentService.submitInstance(request.id);
    }
    
    createTemplateFlow(template: {
        type: AssessmentTemplateEnum, name: string, fromExistingTemplate: boolean,
        dataSourceSpecific: boolean, tagIds?: number[], useCase?: AssessmentUseCaseEnum
    }): Observable<AssessmentsTemplate> {
            const type = this.getAssessmentTemplateType(template);
            // Only MineOS predefined templates carry CMS-stored logic; user-created base
            // templates aren't in FORM_TEMPLATES, so this resolves to undefined (no logic).
            const logic = template.fromExistingTemplate
                ? this.getMineOSTemplateByType(template.type)?.logic
                : undefined;
            return this.createTemplate({
                type,
                name: template.name,
                tagIds: template?.tagIds,
                dataSourceSpecific: template.dataSourceSpecific || type === AssessmentTemplateEnum.Vendor,
                schema: this.getNewTemplateSchemaByType(template.fromExistingTemplate ? template.type : AssessmentTemplateEnum.Empty),
                ...(logic ? { logic } : {})
            } as AssessmentsTemplateRequest);
        }

    private getAssessmentTemplateType(template: {
        type: AssessmentTemplateEnum, name: string, fromExistingTemplate: boolean,
        dataSourceSpecific: boolean, tagIds?: number[], useCase?: AssessmentUseCaseEnum
    }): string {
        if (template.useCase && template.type === AssessmentTemplateEnum.Empty) {
          switch (template.useCase) {
            case AssessmentUseCaseEnum.AI:
              return this.getAiAssessmentTemplateEnumKeyByValue(AssessmentTemplateEnum.AI);
            case AssessmentUseCaseEnum.Privacy:
              return this.getAiAssessmentTemplateEnumKeyByValue(AssessmentTemplateEnum.Empty);
            case AssessmentUseCaseEnum.Vendor:
              return this.getAiAssessmentTemplateEnumKeyByValue(AssessmentTemplateEnum.Vendor);
          }
        }
        return this.getAiAssessmentTemplateEnumKeyByValue(template.type);
    }

    private getNewTemplateSchemaByType(type: AssessmentTemplateEnum): string {
        if (type === AssessmentTemplateEnum.Empty) {
            return this.buildSchemaBySections(this.generateNewSections());
        }

        // Guard against a type not in FORM_TEMPLATES (e.g. a user-created base template passed
        // via fromExistingTemplate): degrade to an empty schema instead of throwing on `.template`.
        const mineTemplate = this.getMineOSTemplateByType(type)?.template;
        if (!mineTemplate) {
            return this.buildSchemaBySections(this.generateNewSections());
        }
        return JSON.stringify(this.transformPredefiendTemplateToSections(mineTemplate as AssessmentsMineOSFormControl[]));
    }
    
    private transformPredefiendTemplateToSections(template: AssessmentsMineOSFormControl[]): AssessmentsMineOSFormControl[] {
        const sections = [];
        for (let section of template) {
          if ([AssessmentQuestionTypeEnum.Group, AssessmentQuestionTypeEnum.NewSection].includes(section.type)) {
            sections.push(section);
          }
          else {
            sections.push({
              ...section,
              type: AssessmentQuestionTypeEnum.Group,
              controls: [{
                ...section
              } as AssessmentsMineOSFormControl]
            } as AssessmentsMineOSFormControl);
          }
        }
        return sections;
    }
    
    private generateNewSections(): AssessmentsMineOSFormControl[] {
        return Array.of(this.generateNewSection() as AssessmentsMineOSFormControl);
    }

    getAssessmentComments(assessmentPageId: AssessmentResourceId): Observable<AssessmentCommentResponse[]> {
        return this.apiClientAssessmentService.getAssessmentComments(assessmentPageId);
    }

    createComment(
        assessmentPageId: AssessmentResourceId, 
        sectionId: AssessmentResourceId, 
        questionId: AssessmentResourceId, 
        comment: AssessmentCommentRequest
    ): Observable<AssessmentCommentResponse> {
        return this.apiClientAssessmentService.createComment(assessmentPageId, sectionId, questionId, comment);
    }

    resolveComment(
        assessmentPageId: AssessmentResourceId, 
        sectionId: AssessmentResourceId, 
        questionId: AssessmentResourceId, 
        commentId: AssessmentResourceId, 
        isResolved: boolean
    ): Observable<AssessmentCommentResponse> {
        return this.apiClientAssessmentService.resolveComment(assessmentPageId, sectionId, questionId, commentId, isResolved);
    }

    getActivityLogs(
        assessmentPageId: AssessmentResourceId,
        options?: {
            questionId?: AssessmentResourceId;
            userId?: AssessmentResourceId;
            before?: string;
            after?: string;
            limit?: number;
        }
    ): Observable<AssessmentActivityLogsResponse> {
        return this.apiClientAssessmentService.getActivityLogs(assessmentPageId, options);
    }

    getAssessmentAuditFilterOptions(assessmentPageId: AssessmentResourceId): Observable<AssessmentAuditFilterOptionsResponse> {
        return this.apiClientAssessmentService.getAssessmentAuditFilterOptions(assessmentPageId);
    }

    selectAllCollaboratorsAssessments(email: string): Observable<AssessmentPage[]> {
        return this.selectAssessments(true).pipe(
            map(assessments => Array.from(assessments.values())
                .filter(assessment => assessment.collabInvites?.some(invite => invite.email === email))
            )
        );
    }

    /**
     * Refetches all assessments from API into the store (e.g. after a system merge re-links PAs to the merged system).
     * Always emits exactly once and never errors: the store logs API failures and completes empty, which is
     * normalised here so callers can chain `first()` / `switchMap` without guarding for an empty stream.
     */
    refreshAssessments(): Observable<void> {
        return this.store.refreshAssessments().pipe(defaultIfEmpty(undefined));
    }

    /** Refetches assessments from API and returns the collaborator's list. Used to refresh sidebar after mark-as-done/undo. */
    refreshCollaborationAssessments(email: string): Observable<AssessmentPage[]> {
        return this.store.refreshAssessments().pipe(
            switchMap(() => this.selectAllCollaboratorsAssessments(email)),
            first()
        );
    }
}